// src/components/LiveBroadcaster.js
// In-browser broadcasting panel for admin — captures camera + mic,
// mixes in background music via Web Audio API, sends to Cloud Run relay.
// Music modes: Off, Music File (MP3 upload), Computer Audio (captures desktop audio)
//
// ── Adaptive quality ──
// Broadcasts start at 4K and the relay continuously reports encoding health
// (fps + speed). If ffmpeg on the relay can't sustain realtime on a given tier,
// the client transparently renegotiates down the ladder (4K → 1080p → 720p)
// without dropping the WebSocket or the live stream. Admin can force a tier
// via the "Broadcast Quality" dropdown if they prefer manual control.

import React, { useState, useEffect, useRef, useCallback } from 'react';
// ── Pre-recorded class buffer (2026-04-23 hardening) ──
// Replaces in-memory chunk accumulation for pre-recorded classes. Survives
// memory pressure, tab close, and component unmount. Used ONLY by the
// prerecorded path — live classes continue to stream chunks directly to
// the WebSocket relay with no IDB involvement.
import {
    startRecording as bufferStart,
    appendChunk as bufferAppend,
    flushChunks as bufferFlush,
    getRecordingBlob as bufferGetBlob,
    clearRecording as bufferClear,
    estimateStorage as bufferEstimate,
} from '../services/recordingBuffer';

const RELAY_URL = process.env.REACT_APP_LIVE_RELAY_URL || '';

// ── Pre-recorded hardening thresholds (2026-04-23) ──
// HEARTBEAT_STALE_MS: how long without a chunk before the "recording stalled"
//   warning surfaces. 2500ms is 10× the 250ms timeslice — well above any
//   normal jitter, comfortably below the point where admin would notice on
//   their own. Shorter would false-positive during GC pauses; longer would
//   let the admin keep recording into a silent stop for too long.
// STORAGE_WARN_BYTES: below this, we warn before starting. 3 GB covers a
//   90-min class at 4K (~9 GB margin above the typical case; conservative
//   because browsers reserve quota for other origins too).
const HEARTBEAT_STALE_MS = 2500;
const STORAGE_WARN_BYTES = 3 * 1024 * 1024 * 1024;

// ── Quality tier ladder ──
// Ordered highest → lowest. Downshifts walk this list in order.
// videoBps is a MediaRecorder target; the camera's actual delivered resolution
// may be lower than the request (constraint is `ideal`, not `exact`), so we
// clamp bitrate to the tier that matches the real videoWidth post-getUserMedia.
const QUALITY_TIERS = {
    '4k':   { key: '4k',   width: 3840, height: 2160, videoBps: 15000000, audioBps: 192000, label: '4K'    },
    '1080': { key: '1080', width: 1920, height: 1080, videoBps:  4500000, audioBps: 160000, label: '1080p' },
    '720':  { key: '720',  width: 1280, height:  720, videoBps:  2500000, audioBps: 128000, label: '720p'  },
};
const TIER_ORDER = ['4k', '1080', '720'];
const nextLowerTier = (tier) => {
    const idx = TIER_ORDER.indexOf(tier);
    return idx >= 0 && idx < TIER_ORDER.length - 1 ? TIER_ORDER[idx + 1] : null;
};
// Pick the highest tier whose target width is ≤ actual delivered videoWidth.
// Keeps bitrate sensible if the camera only delivers 1080p even when we asked for 4K.
const tierForActualWidth = (actualWidth, cap = '4k') => {
    const capIdx = TIER_ORDER.indexOf(cap);
    for (let i = capIdx; i < TIER_ORDER.length; i++) {
        const t = QUALITY_TIERS[TIER_ORDER[i]];
        if (actualWidth >= t.width) return t.key;
    }
    return '720';
};

// Downshift thresholds — consult SPEED_SAMPLE_COUNT most recent status messages
// from the relay. These are deliberately conservative: we want to downshift only
// when ffmpeg is reliably behind realtime, not on a single transient blip.
const SPEED_SAMPLE_COUNT = 8;
const DOWNSHIFT_SLOW_THRESHOLD = 0.85;  // samples below this count as "slow"
const DOWNSHIFT_SLOW_MIN_COUNT = 5;     // trigger if ≥5 of last 8 are slow
const DOWNSHIFT_CRITICAL_SPEED = 0.5;   // single sample ≤ this = instant trigger
const DOWNSHIFT_LOW_FPS = 18;           // fps floor before counting as bad
const DOWNSHIFT_COOLDOWN_MS = 20000;    // wait this long after a change before another

// ── D-054: Auto-reconnect tuning ──
// Exponential backoff schedule (ms): 1s, 2s, 4s, 8s, 16s, 30s, 30s, 30s, 30s, 30s
// — capped at 30s per attempt, total window ≈ 3 minutes. Matches Mux's default
// `reconnect_window: 60` × 3 (60s per Mux reconnect × 3 attempts we can afford
// before Mux itself seals the manifest). Abandoning after 3 min keeps viewers
// from sitting on a black player indefinitely.
const RECONNECT_BACKOFF_MS = [1000, 2000, 4000, 8000, 16000, 30000, 30000, 30000, 30000, 30000];
const RECONNECT_DEADLINE_MS = 3 * 60 * 1000; // 3 minutes total

// ── Broadcast audio mix (2026-04-24 same-day — revision 2 after real-world test) ──
// Podcast-production-standard broadcast chain for coaching/fitness content.
// Voice is primary; music sits ~14 dB below. Pipeline is IMMUNE to the
// admin's system volume, music app volume, and mic choice.
//
// Chain per side (both music modes; Off mode bypasses Web Audio entirely):
//   VOICE:  micSource → highPass(80Hz) → leveler → makeup(2×) → limiter(-3dB)
//                    → voiceGain(1.0)  → [analyser tap] → mixDest
//   MUSIC:  musicSource → leveler → makeup(1.4×) → limiter(-3dB)
//                      → musicGain(0.20) → [analyser tap] → mixDest
//
// Why this specific chain:
//
//   1. HIGH-PASS FILTER (voice only, 80 Hz) — removes sub-audible rumble
//      (breathing, mic handling, footsteps). Inaudible to the ear; cleans
//      up the low end before compression sees it. Standard on every
//      professional voice mic.
//
//   2. LEVELER (gentle compressor, knee 12 dB) — brings quiet voices up
//      and loud peaks down without squashing. Slow enough to preserve
//      natural voice dynamics / musical transients. Replaces the prior
//      revision's 8:1 "industrial squasher" that caused the chamber/echo
//      artifact Danielle reported on the first broadcast — 8:1 ratio with
//      10ms attack catches every beat and pumps audibly.
//
//   3. MAKEUP GAIN — post-leveler boost. Voice: 2.0× (~+6 dB) compensates
//      for quiet-lavalier inputs (-35 dBFS typical) — resolves the
//      reported "USB-C lavalier muffled/quiet" symptom. Bluetooth
//      headset mics output hot because they have hardware AGC built in;
//      USB lavaliers are raw. Our software leveler+makeup replicates
//      what the Bluetooth mic's hardware does. Music makeup: 1.4×
//      (~+3 dB) — lighter because music doesn't have the same
//      quiet-source problem.
//
//   4. PEAK LIMITER (shared pattern, -3 dB ceiling) — transparent until
//      the signal is about to clip; catches transients the leveler
//      misses. Fast attack (1ms), short release (50ms). One per chain
//      so they operate independently.
//
//   5. BUS GAIN — fixed voice:music ratio. Voice at unity (1.0); music at
//      0.20 (~14 dB below voice). Reduced from 0.25 after real-world
//      testing showed music was "a notch too loud."
//
// On AGC behavior: the leveler's attack is deliberately not super-slow.
// Voice attack 20ms catches vocal peaks; music attack 50ms preserves
// musical transients. "Slow" in broadcast terms is relative — these
// values act like AGC over spoken phrases and musical bars, not the
// full-song scale. This prevents pumping while still leveling.
//
// Tuning guide (edit values below if real-world testing says so):
//   • Voice still too quiet → raise voiceMakeup to 2.5-3.0
//   • Voice too hot / clipping → lower voiceMakeup to 1.5, or lower
//     voiceLeveler threshold to -30 (more leveling)
//   • Music too loud → lower musicBusGain (0.18 gives ~-15 dB)
//   • Music too soft → raise musicBusGain to 0.25 (~-12 dB)
//   • Music pumping / unnatural → lower musicLeveler ratio to 1.5,
//     raise release to 0.80
const MIX_CONFIG = {
    // Voice chain
    voiceHighPassFreq: 80,       // Hz — removes sub-audible rumble
    voiceLeveler: { threshold: -24, ratio: 3, knee: 12, attack: 0.02, release: 0.20 },
    voiceMakeup: 8.0,            // ~+18 dB — compensates for quiet lavaliers
                                  // Tuning history (2026-04-24):
                                  //   v1: 2.0 (+6 dB) — assumed -25 dBFS lavalier input
                                  //   v2: 5.0 (+14 dB) — real-world USB-C lavalier
                                  //     measured at -35 to -40 dBFS at coaching distance.
                                  //   v3: 8.0 (+18 dB) — second real-world test still
                                  //     reported voice "two notches too low." Lavalier
                                  //     is quieter than expected. Peak limiter at -3 dB
                                  //     catches loud close-talking, so even at +18 dB
                                  //     makeup the broadcast can't clip.
                                  // If still too quiet → next stop is 10.0 (+20 dB).
                                  // Beyond that, consider also lowering voiceLeveler
                                  // threshold to -30 so compression engages on more of
                                  // the signal (more uniform makeup effect).

    // Music chain
    musicLeveler: { threshold: -24, ratio: 2, knee: 12, attack: 0.05, release: 0.50 },
    musicMakeup: 1.4,            // ~+3 dB — gentle boost, preserves dynamics

    // Shared peak limiter — transparent until about to clip
    peakLimiter: { threshold: -3, ratio: 20, knee: 0, attack: 0.001, release: 0.05 },

    // Fixed bus gains — voice primary, music ~14 dB below
    voiceBusGain: 1.0,
    musicBusGain: 0.14,          // Tuning history:
                                  //   v1 (2026-04-24): 0.25 (~-12 dB) — initial guess
                                  //   v2 (2026-04-24): 0.20 (~-14 dB) — first real-world test
                                  //   v3 (2026-04-24): 0.18 (~-15 dB) — second test, "one notch quieter"
                                  //   v4 (2026-04-25): 0.14 (~-17 dB) — third test, opted for a
                                  //     ~2 dB perceptible drop (a 1 dB step would have been at
                                  //     the edge of audibility for real-world listening).
                                  // This is a pure gain stage AFTER the limiter, so each
                                  // step down is a uniform attenuation — zero impact on
                                  // music quality, just lower in the broadcast mix.
};

function createVoiceHighPass(ctx) {
    const f = ctx.createBiquadFilter();
    f.type = 'highpass';
    f.frequency.value = MIX_CONFIG.voiceHighPassFreq;
    f.Q.value = 0.7; // Butterworth response — flat pass-band, smooth rolloff
    return f;
}

function createVoiceLeveler(ctx) {
    const c = ctx.createDynamicsCompressor();
    const cfg = MIX_CONFIG.voiceLeveler;
    c.threshold.value = cfg.threshold;
    c.ratio.value = cfg.ratio;
    c.knee.value = cfg.knee;
    c.attack.value = cfg.attack;
    c.release.value = cfg.release;
    return c;
}

function createMusicLeveler(ctx) {
    const c = ctx.createDynamicsCompressor();
    const cfg = MIX_CONFIG.musicLeveler;
    c.threshold.value = cfg.threshold;
    c.ratio.value = cfg.ratio;
    c.knee.value = cfg.knee;
    c.attack.value = cfg.attack;
    c.release.value = cfg.release;
    return c;
}

function createPeakLimiter(ctx) {
    const c = ctx.createDynamicsCompressor();
    const cfg = MIX_CONFIG.peakLimiter;
    c.threshold.value = cfg.threshold;
    c.ratio.value = cfg.ratio;
    c.knee.value = cfg.knee;
    c.attack.value = cfg.attack;
    c.release.value = cfg.release;
    return c;
}

// ── Camera zoom picker (2026-04-24) ──
// iPhone via Continuity Camera defaults to the Wide (1×) lens. At typical
// coaching distances (8–10 ft) that lens can't fit a full standing body in
// frame. The MediaStreamTrack API exposes a `zoom` capability that maps
// directly to iPhone's native 0.5× / 1× / 2× lens picker — 0.5× switches to
// Ultra Wide, 2× to Telephoto (or digital crop on non-Pro models). USB
// webcams and the MacBook built-in FaceTime camera don't expose this
// capability, so the picker is auto-hidden for them via the
// `zoomCapability` gate in the render. Values outside a camera's reported
// range render as disabled chips rather than hidden ones, keeping the
// option surface consistent across devices.
//
// Applied via `track.applyConstraints({ advanced: [{ zoom: N }] })` — the
// `advanced:` form is best-effort so a rejection of one specific value
// doesn't blow away the whole constraints object. `applyConstraints` is
// in-place on the existing track; it does NOT tear down the stream or
// MediaRecorder, so mid-broadcast zoom changes are safe.
const ZOOM_OPTIONS = [0.5, 0.75, 1, 2];

function readZoomCapability(track) {
    try {
        const caps = typeof track?.getCapabilities === 'function' ? track.getCapabilities() : null;
        // Diagnostic log (2026-04-24 same-day revision): some browser/OS/device
        // combos don't expose `zoom` in getCapabilities even when the underlying
        // camera supports it (notably iPhone via Continuity Camera on Edge — a
        // user report that surfaced after ship). Logging once per track read
        // lets admin check the Console to verify whether the browser is
        // surfacing the capability. Single line, no PII, safe in production.
        if (caps) {
            const settings = typeof track.getSettings === 'function' ? track.getSettings() : {};
            console.log('[MoM zoom-capability]', {
                label: track.label || '(unlabeled)',
                deviceId: (settings.deviceId || '').slice(0, 12) + '…',
                hasZoom: 'zoom' in caps,
                zoom: caps.zoom || null,
                capabilityKeys: Object.keys(caps).sort(),
            });
        }
        const z = caps?.zoom;
        if (!z || typeof z.min !== 'number' || typeof z.max !== 'number' || z.max <= z.min) return null;
        return { min: z.min, max: z.max, step: z.step || 0.01 };
    } catch (_) {
        return null;
    }
}

async function applyZoomToTrack(track, value) {
    if (!track || typeof track.applyConstraints !== 'function') return false;
    if (track.readyState !== 'live') return false;
    try {
        // Intentionally only sets `zoom` — no width/height/aspectRatio — so
        // the 16:9 ideal set at stream creation (2026-04-23) is preserved.
        await track.applyConstraints({ advanced: [{ zoom: value }] });
        return true;
    } catch (_) {
        return false;
    }
}

function zoomStorageKey(deviceId) {
    return `mom-camera-zoom-${deviceId || ''}`;
}

// Smart default zoom for a camera with no stored preference (2026-04-25):
// if the camera supports ultra-wide (cap.min <= 0.5 — virtually only iPhone
// via Continuity Camera and a few prosumer mirrorless cameras), default to
// 0.5x for a wider field of view. Otherwise default to 1x (the camera's
// native). This addresses the workflow where admin selects iPhone without
// first setting 0.5x in the iOS Camera app — we now do it automatically.
// User can still pick any chip; once they do, that choice is stored and
// honored on all future sessions, overriding the smart default.
function smartDefaultZoom(cap) {
    if (!cap) return 1;
    if (cap.min <= 0.5) return 0.5;
    return 1;
}

function readStoredZoom(deviceId, cap) {
    if (!deviceId || !cap) return 1;
    try {
        const raw = localStorage.getItem(zoomStorageKey(deviceId));
        if (raw !== null) {
            const parsed = parseFloat(raw);
            if (!Number.isFinite(parsed)) {
                // Corrupt value — clear and fall through to smart default.
                try { localStorage.removeItem(zoomStorageKey(deviceId)); } catch (_) { /* noop */ }
            } else if (parsed < cap.min || parsed > cap.max) {
                // Stored value is out of range for this camera (capabilities may
                // have changed OS-side, or the stored value is from a different
                // physical device reusing the same deviceId — unlikely but
                // harmless to clear). Fall through to smart default.
                try { localStorage.removeItem(zoomStorageKey(deviceId)); } catch (_) { /* noop */ }
            } else {
                // Honor the user's explicit stored preference.
                return parsed;
            }
        }
        // No (usable) stored preference — apply the smart default for this camera.
        return smartDefaultZoom(cap);
    } catch (_) {
        // localStorage unavailable (some private-browsing contexts) — fall back
        // to smart default without touching storage.
        return smartDefaultZoom(cap);
    }
}

function LiveBroadcaster({
    rtmpUrl, streamKey, onBroadcastStart, onBroadcastEnd, endingAt = null,
    // ── Class type awareness (Option A unification — extended 2026-04-20) ──
    // Both class types now share the same hardened "End Class" control:
    //   'live'        — admin sees "Start Broadcasting" and, while broadcasting,
    //                   an "End Class" button that routes through the parent's
    //                   seal-before-stop flow via `onEndClassRequest` → confirm
    //                   modal → handleEndClass.
    //   'prerecorded' — admin sees "Start Recording" and, while broadcasting,
    //                   an "End Class" button that routes through the same
    //                   seal-before-stop flow → handleEndClass detects the
    //                   pre-recorded RECORDING phase and delegates to
    //                   handleEndRecording (identical /complete-first ordering,
    //                   but doesn't flip class state to 'ending'/'ended' —
    //                   scheduledAt auto-flip handles the live transition).
    //
    // The prior live-only "Stop Broadcasting" button (direct stopBroadcast call,
    // no manifest seal) was a footgun — if admin clicked it before the End Class
    // control above the player, MediaRecorder stopped while the Mux manifest was
    // still open, leaving the same silent-source-but-manifest-open window that
    // produced slate on pre-recorded VODs. Retired 2026-04-20 for visual+
    // behavioral parity across both class types. `stopBroadcast` itself remains
    // reachable internally (endingAt effect, reconnect exhaustion, unmount).
    classType = 'live',
    // ── classId (pre-recorded hardening, 2026-04-23) ──
    // Pre-recorded classes key their IDB recording buffer by classId so chunks
    // are unambiguously scoped to one recording session and recovery-on-mount
    // can pair surviving chunks with the correct class document. Unused by live
    // classes (live chunks stream straight to the relay, never touch IDB).
    classId = null,
    onEndClassRequest, // called when admin clicks End Class inside the broadcaster (both class types)
    // ── Mux Direct Upload path (pre-recorded only, introduced 2026-04-21) ──
    // When `classType === 'prerecorded'`, LiveBroadcaster records MediaRecorder
    // chunks to an in-memory array instead of streaming them to the WebSocket
    // relay. On stop, we assemble the final Blob and hand it to the parent via
    // this callback. Parent (Workouts.js) then PUTs the Blob to a Mux Direct
    // Upload URL — zero live-ingest artifacts, pristine source quality.
    // Signature: (blob: Blob, mimeType: string) => void
    // Fires exactly once per recording session (between stopBroadcast firing
    // MediaRecorder.stop() and onBroadcastEnd firing). Unused for live classes.
    onRecordingComplete,
    // ── D-053: BroadcasterCueSequence lifecycle callbacks ──
    // Parent (Workouts.js) uses these to drive the 5-step "going live" cue instead of
    // a blind 10s timer. Each callback fires exactly once per broadcast session (from
    // a fresh startBroadcast) and is reset on stopBroadcast / unexpected disconnect.
    // All three are optional — existing call sites without these props degrade to no-ops.
    onWsOpen,         // fires when the WebSocket to the relay opens (TCP + WS handshake done)
    onMuxConnected,   // fires when relay reports type:'connected' (RTMP → Mux handshake done)
    onStabilized,     // fires after ~3 consecutive healthy status samples (fps/speed good)
}) {
    // Device selection
    const [cameras, setCameras] = useState([]);
    const [mics, setMics] = useState([]);
    const [selectedCamera, setSelectedCamera] = useState('');
    const [selectedMic, setSelectedMic] = useState('');
    const [devicesLoaded, setDevicesLoaded] = useState(false);

    // ── Camera zoom state (2026-04-24) ──
    // `zoomCapability` reflects what the active camera reports via
    // track.getCapabilities().zoom — null for cameras without zoom (USB
    // webcams, MacBook FaceTime), populated for iPhone via Continuity
    // Camera. The picker UI is rendered only when this is non-null.
    // `zoomValue` is the user-facing current zoom; mirrored to a ref so
    // async re-apply paths (post-broadcast start, post-tier-downshift)
    // read a fresh value instead of a closed-over one — same pattern as
    // activeTierRef / tierPinRef / classTypeRef.
    const [zoomCapability, setZoomCapability] = useState(null);
    const [zoomValue, setZoomValue] = useState(1);
    const zoomValueRef = useRef(1);

    // Broadcast state
    const [broadcasting, setBroadcasting] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [error, setError] = useState('');
    const [elapsed, setElapsed] = useState(0);

    // ── D-054: Auto-reconnect state ──
    // `reconnecting` is true during the backoff window (1s → 30s exp). Admin sees
    // a "Reconnecting…" banner instead of the broken-connection error. `reconnectRemainingMs`
    // drives the countdown in the banner copy. Refs mirror state for async handlers.
    const [reconnecting, setReconnecting] = useState(false);
    const [reconnectRemainingMs, setReconnectRemainingMs] = useState(RECONNECT_DEADLINE_MS);
    const reconnectingRef = useRef(false);
    const reconnectAttemptsRef = useRef(0);
    const reconnectDeadlineRef = useRef(0);
    const reconnectTimerRef = useRef(null);
    const reconnectCountdownRef = useRef(null);
    // Admin-initiated stop flag — set true inside stopBroadcast so the ws.onclose
    // handler knows NOT to reconnect (vs. an upstream disconnect where it should).
    const adminStoppedRef = useRef(false);

    // ── Mux Direct Upload: IndexedDB-backed chunk buffer (pre-recorded only) ──
    // Replaces the 2026-04-21 in-memory array (recordedChunksRef). MediaRecorder's
    // ondataavailable handler writes each chunk into IndexedDB via the
    // recordingBuffer service. On stop, we flush pending writes and assemble a
    // composite Blob from IDB for upload. Survives memory pressure, tab close,
    // and component unmount. See src/services/recordingBuffer.js for schema
    // and ordering semantics. Unused for live classes.
    //
    // Two refs remain in-memory:
    //   • classIdRef — mirrors the `classId` prop so ondataavailable (whose
    //     closure is fixed at recorder-build time) can read the fresh value.
    //   • totalBytesCapturedRef — fire-and-forget byte accumulator for the
    //     heartbeat UI (we'd otherwise have to read IDB on every UI tick).
    const classIdRef = useRef(classId);
    useEffect(() => { classIdRef.current = classId; }, [classId]);
    const totalBytesCapturedRef = useRef(0);

    // ── Pre-recorded observability / heartbeat state (2026-04-23) ──
    // lastChunkAtRef: updated in ondataavailable with Date.now() whenever a
    //   chunk is written. The heartbeat timer reads it to compute "time since
    //   last frame" without triggering a re-render on every chunk.
    // heartbeatTick: state-backed tick counter, incremented once/second by the
    //   heartbeat timer. Triggers UI re-render so the "Captured / Last frame"
    //   row stays current. Cheaper than storing lastChunkAt in state directly
    //   (avoids the ~4 Hz render cadence chunks would cause).
    // recorderErrorMsg: populated by recorder.onerror and track .onended. When
    //   set, the broadcaster UI switches the red BROADCASTING chip to a
    //   "Recording stalled" warning with a "Save now" CTA that triggers the
    //   same End Class → finalize → upload flow as a graceful stop.
    const lastChunkAtRef = useRef(0);
    const [heartbeatTick, setHeartbeatTick] = useState(0);
    const [recorderErrorMsg, setRecorderErrorMsg] = useState('');

    // ── Screen Wake Lock (pre-recorded only) ──
    // During pre-recorded recording there's no outbound network traffic (no
    // WebSocket to the relay — unlike live broadcasts), so the OS treats the
    // tab as idle and may sleep the screen, suspend the tab, or reclaim the
    // camera. Wake Lock API tells the OS explicitly: keep this tab active.
    // Chrome + Edge support it fully; Safari 16.4+; Firefox via flag. On
    // unsupported browsers the request silently fails and we fall back to a
    // prominent "keep this tab focused" banner. Released in stopBroadcast.
    const wakeLockRef = useRef(null);

    // (2026-04-23) the prior `recordedChunksRef` in-memory array is gone.
    // Prerecorded chunks now live in IndexedDB via the recordingBuffer service;
    // live chunks are written straight to the WebSocket relay in ondataavailable.
    // Neither path needs an in-memory buffer.
    // classType mirrored to a ref so the ondataavailable closure (set at
    // recorder-build time, long before a reconnect or tier swap) reads the fresh
    // value. Same pattern as wsRef — handlers capture props at build time and
    // would otherwise read a stale value after prop changes.
    const classTypeRef = useRef(classType);
    useEffect(() => { classTypeRef.current = classType; }, [classType]);
    // Callback ref for onRecordingComplete — the async MediaRecorder 'stop' handler
    // in stopBroadcast reads this at fire time (well after the outer useCallback
    // closed over an older value). onRecordingComplete is a parent-supplied callback
    // and its identity is stable in practice, but the ref removes any doubt.
    const onRecordingCompleteRef = useRef(onRecordingComplete);
    useEffect(() => { onRecordingCompleteRef.current = onRecordingComplete; }, [onRecordingComplete]);

    // ── D-053: Cue sequence lifecycle tracking ──
    // These refs ensure each callback fires exactly once per broadcast session. A
    // reconnect after an unexpected drop does NOT re-fire them (the cue has already
    // advanced past those steps and we don't want to reset it). Reset fires from
    // startBroadcast (fresh session) and stopBroadcast (admin ended).
    const wsOpenFiredRef = useRef(false);
    const muxConnectedFiredRef = useRef(false);
    const stabilizedFiredRef = useRef(false);
    const goodSampleCountRef = useRef(0);
    const onWsOpenRef = useRef(onWsOpen);
    const onMuxConnectedRef = useRef(onMuxConnected);
    const onStabilizedRef = useRef(onStabilized);
    useEffect(() => { onWsOpenRef.current = onWsOpen; }, [onWsOpen]);
    useEffect(() => { onMuxConnectedRef.current = onMuxConnected; }, [onMuxConnected]);
    useEffect(() => { onStabilizedRef.current = onStabilized; }, [onStabilized]);

    // Encoding health — updated by relay status messages during broadcast
    const [encodingStatus, setEncodingStatus] = useState(null); // { fps, speed } or null
    const broadcastingRef = useRef(false); // tracks broadcast state for async handlers

    // ── Adaptive quality state ──
    // `tierPin` is the admin's chosen mode: 'auto' lets us start at 4K and downshift
    // as needed; '4k'/'1080'/'720' lock the broadcast to that tier (no auto-adjust).
    const [tierPin, setTierPin] = useState('auto');
    // `activeTier` is the tier currently broadcasting — updates on downshift so the
    // "Now broadcasting at Xp" label stays honest.
    const [activeTier, setActiveTier] = useState('4k');
    // Toast message shown briefly when a tier change occurs (e.g. auto-downshift).
    const [tierNotice, setTierNotice] = useState('');
    // Whether a downshift is currently in progress — gates the health evaluator so
    // we don't try to downshift twice in parallel.
    const [adjustingTier, setAdjustingTier] = useState(false);
    const activeTierRef = useRef('4k');
    const adjustingTierRef = useRef(false);
    const tierPinRef = useRef('auto');
    const speedSamplesRef = useRef([]); // rolling window of { speed, fps } samples
    const lastTierChangeAtRef = useRef(0); // ms timestamp of last tier change
    // Split refs for video vs audio tracks so a video-only downshift can replace
    // the video track without disturbing the audio graph (mic/music/computer mix).
    const videoTrackRef = useRef(null);
    const audioTracksRef = useRef([]);
    const outputStreamRef = useRef(null);

    // Music state — three modes: 'off', 'file', 'computer'
    // Default: 'computer' when supported (most coaching workflows use a music
    // app like Spotify) — falls back to 'off' on Safari since Safari doesn't
    // support getDisplayMedia for tab/system audio capture (2026-04-25).
    const [musicMode, setMusicMode] = useState(() => {
        if (typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getDisplayMedia === 'function') {
            return 'computer';
        }
        return 'off';
    });
    // NOTE (2026-04-24 same-day audio revision): `musicVolume` state + slider
    // were retired. Broadcast mix is now fixed at MIX_CONFIG.musicBusGain
    // post-compressor and decoupled from admin's local listening volume.
    // See MIX_CONFIG at top of file for rationale.
    const [musicFile, setMusicFile] = useState(null);
    const [musicPlaying, setMusicPlaying] = useState(false);
    const [musicFileName, setMusicFileName] = useState('');
    // Audio levels (0-1) sampled from AnalyserNodes tapping each path post-limiter
    // — drives the live meters so admin can VERIFY both voice and music are actually
    // flowing during broadcast. Flat-zero on either meter is an instant visual cue
    // that something upstream broke (AudioContext suspended, mic disconnected,
    // Share-audio not toggled, source app paused, etc.).
    const [audioLevel, setAudioLevel] = useState(0);    // music path level
    const [voiceLevel, setVoiceLevel] = useState(0);    // voice path level (2026-04-24 rev 2)

    // Refs
    const previewRef = useRef(null);
    const wsRef = useRef(null);
    const recorderRef = useRef(null);
    const streamRef = useRef(null);
    const displayStreamRef = useRef(null); // for computer audio capture
    const audioCtxRef = useRef(null);
    const musicSourceRef = useRef(null);
    const musicGainRef = useRef(null);
    const musicElementRef = useRef(null);
    const audioLevelAnalyserRef = useRef(null); // AnalyserNode parallel-tapped off the music/computer path (post-limiter)
    const voiceLevelAnalyserRef = useRef(null); // AnalyserNode parallel-tapped off the voice path (post-limiter, 2026-04-24 rev 2)

    // ── Test Mix state (2026-04-25) ──
    // Pre-broadcast mix verification. Builds the SAME audio graph as
    // startBroadcast (voice + music chains, full leveler/makeup/limiter
    // pipeline) but with NO MediaRecorder, NO WebSocket, NO output
    // destination. Pure silent monitoring so admin can verify mic + music
    // levels look right BEFORE clicking Start Broadcasting. testCleanupRef
    // is the teardown closure populated by startTestMix; called from
    // stopTestMix, from startBroadcast (must end test before broadcast),
    // from unmount, and from any state change that invalidates the test
    // graph (camera, mic, music mode, file).
    const [testingMix, setTestingMix] = useState(false);
    const testCleanupRef = useRef(null);
    // Holds a getDisplayMedia stream acquired during a Test Mix click, so a
    // subsequent Start Broadcasting can reuse it without re-prompting the
    // user for screen share (2026-04-25). Lifecycle:
    //   • Set: at end of successful startTestMix in computer mode
    //   • Cleared by stopTestMix() (manual stop / camera/mic/mode/file change
    //     / unmount) — releaseShare flag controls whether tracks are stopped
    //   • Claimed by startBroadcast computer-mode branch (transfers
    //     ownership to displayStreamRef which the broadcast lifecycle
    //     manages) — preAcquired ref is nulled on claim
    //   • Auto-cleared if the user clicks the browser's "Stop sharing"
    //     overlay during the test (audio track 'ended' listener)
    const preAcquiredDisplayStreamRef = useRef(null);
    const timerRef = useRef(null);
    const startTimeRef = useRef(null);

    // ── Enumerate available cameras and microphones ──
    // (2026-04-23) Hot-plug support: the original implementation ran
    // enumerateDevices() exactly once on mount, so any device that appeared
    // AFTER page load — notably iPhone via Continuity Camera, which takes
    // seconds-to-minutes to negotiate the Wi-Fi Direct link after the Mac
    // wakes — was invisible in the camera dropdown. Photo Booth and system
    // camera pickers work because they re-enumerate every time the user
    // opens them; web apps have to subscribe to the platform's devicechange
    // event to get the same behaviour.
    //
    // Fix: listen for `devicechange` on `navigator.mediaDevices` and
    // re-enumerate whenever it fires. Covers:
    //   • Continuity Camera iPhone connecting / disconnecting
    //   • USB webcams and mics hot-plugged during a session
    //   • Bluetooth audio devices connecting mid-session
    //   • External display audio sources that appear after DisplayLink etc.
    //
    // The getUserMedia permission prompt only needs to happen once; we
    // gate it with `permissionPromptedRef` so devicechange-triggered
    // re-enumerations don't re-prompt.
    const permissionPromptedRef = useRef(false);
    useEffect(() => {
        let cancelled = false;

        async function loadDevices(isInitial) {
            try {
                if (isInitial && !permissionPromptedRef.current) {
                    permissionPromptedRef.current = true;
                    const tempStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
                    tempStream.getTracks().forEach((t) => t.stop());
                }
                const devices = await navigator.mediaDevices.enumerateDevices();
                if (cancelled) return;
                setCameras(devices.filter((d) => d.kind === 'videoinput'));
                setMics(devices.filter((d) => d.kind === 'audioinput'));
                setDevicesLoaded(true);
            } catch (e) {
                // Only surface the permission error on the initial prompt —
                // a failed mid-session devicechange re-enumeration is silent
                // (the cached device list from the prior successful call is
                // still valid; devicechange will fire again once the OS
                // settles).
                if (!cancelled && isInitial) {
                    setError('Please allow camera and microphone access to broadcast.');
                }
            }
        }

        loadDevices(true);

        const onDeviceChange = () => { loadDevices(false); };
        if (navigator.mediaDevices?.addEventListener) {
            navigator.mediaDevices.addEventListener('devicechange', onDeviceChange);
        }

        return () => {
            cancelled = true;
            if (navigator.mediaDevices?.removeEventListener) {
                navigator.mediaDevices.removeEventListener('devicechange', onDeviceChange);
            }
        };
    }, []);

    // Manual "Refresh devices" trigger — belt-and-suspenders for any edge
    // case where devicechange doesn't fire (historically Firefox + some
    // Chromium versions have been inconsistent). Exposed as a small button
    // next to the camera dropdown.
    const [refreshingDevices, setRefreshingDevices] = useState(false);
    const refreshDevices = useCallback(async () => {
        setRefreshingDevices(true);
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            setCameras(devices.filter((d) => d.kind === 'videoinput'));
            setMics(devices.filter((d) => d.kind === 'audioinput'));
        } catch (e) { /* non-critical — keep existing cached list */ }
        finally {
            // Brief delay so the spin animation reads as deliberate feedback
            // rather than an imperceptible flicker.
            setTimeout(() => setRefreshingDevices(false), 400);
        }
    }, []);

    // ── Camera zoom helpers (2026-04-24) ──
    // Called from three places:
    //   (1) preview effect, after a fresh stream is created on camera change
    //   (2) startBroadcast, after the broadcast video track is acquired
    //   (3) attemptDownshift, after a new (lower-tier) video track replaces
    //       the old one during a mid-broadcast quality adjustment.
    // Cases (1) and (2) use the stored preference (preferCurrent=false);
    // case (3) preserves the current in-flight zoom (preferCurrent=true) so
    // a tier downshift doesn't reset the framing the admin just chose.
    const initZoomForTrack = useCallback(async (track, deviceId, preferCurrent) => {
        const cap = readZoomCapability(track);
        setZoomCapability(cap);
        if (!cap) {
            // Camera doesn't expose zoom — reset display state to 1× but do
            // NOT touch localStorage (a FaceTime cam and an iPhone at 0.5×
            // coexist in storage; switching between them must preserve
            // each camera's own preference).
            setZoomValue(1);
            zoomValueRef.current = 1;
            return;
        }
        const target = preferCurrent
            ? Math.max(cap.min, Math.min(cap.max, zoomValueRef.current))
            : readStoredZoom(deviceId, cap);
        setZoomValue(target);
        zoomValueRef.current = target;
        // Apply only when non-default — a fresh track is already at 1× so a
        // no-op applyConstraints is wasted work.
        if (target !== 1) {
            const ok = await applyZoomToTrack(track, target);
            if (!ok) {
                // Apply failed (track not live, constraint rejected, permissions
                // revoked, etc.). Revert in-memory state to 1× so the chip
                // selection matches the camera's actual framing — otherwise the
                // user sees the "0.5×" chip highlighted while the preview is at
                // native 1×, which is confusing. We intentionally do NOT clear
                // the stored preference: the camera may accept it next time
                // (a subsequent getUserMedia / mount may succeed), and clearing
                // it would silently throw away the admin's chosen framing.
                setZoomValue(1);
                zoomValueRef.current = 1;
            }
        }
    }, []);

    // Chip click handler — applies the selected zoom to whichever track is
    // currently live (preview OR broadcast), persists per-deviceId, and
    // reverts in-memory state if the track refuses the constraint.
    const onZoomChipClick = useCallback(async (v) => {
        if (!zoomCapability) return;
        if (v < zoomCapability.min || v > zoomCapability.max) return; // disabled chip — safety
        // Prefer the broadcast track if one exists (we're recording/streaming);
        // fall back to the preview stream's video track otherwise.
        const track = videoTrackRef.current || streamRef.current?.getVideoTracks?.()[0];
        if (!track) return;
        const prev = zoomValueRef.current;
        setZoomValue(v);
        zoomValueRef.current = v;
        const ok = await applyZoomToTrack(track, v);
        if (!ok) {
            // Track rejected the constraint — revert in-memory state, do NOT
            // persist. Keeps the chip state honest with what the camera
            // actually applied.
            setZoomValue(prev);
            zoomValueRef.current = prev;
            return;
        }
        // Persist per-camera. Always store the chosen value (was: cleared key
        // for v === 1 to keep storage tidy) — the smart default in
        // readStoredZoom now uses absence-of-key to mean "no preference, apply
        // smart default." If we cleared the key when the user clicked 1x for
        // an iPhone (smart default = 0.5x), the next session would re-apply
        // 0.5x and silently undo their explicit choice. Storing 1x explicitly
        // makes the override stick.
        try {
            localStorage.setItem(zoomStorageKey(selectedCamera), String(v));
        } catch (_) { /* private browsing / storage full — silent */ }
    }, [zoomCapability, selectedCamera]);

    // Auto-select first devices
    useEffect(() => {
        if (cameras.length > 0 && !selectedCamera) setSelectedCamera(cameras[0].deviceId);
    }, [cameras, selectedCamera]);
    useEffect(() => {
        if (mics.length > 0 && !selectedMic) setSelectedMic(mics[0].deviceId);
    }, [mics, selectedMic]);

    // ── Audio-level meter sampler ──
    // Reads BOTH analyser taps (voice path + music path, set up in startBroadcast
    // for 'file' and 'computer' music modes, 2026-04-24 rev 2) ~30fps while
    // broadcasting. Updates `voiceLevel` and `audioLevel` state (0-1) which drive
    // the two live meters in the UI. When a suspended AudioContext is producing
    // NO audio, an analyser returns all zeros and the corresponding meter stays
    // flat — giving admin an instant visual cue that something upstream broke
    // (mic disconnect, source app paused, Share-audio not toggled).
    useEffect(() => {
        // Sample whenever broadcasting OR test-mode is active (2026-04-25). Each
        // path is sampled independently — voice always (when broadcasting OR
        // testingMix), music only when its analyser ref is set (which is
        // controlled by mode + which graph wired it up). The early return
        // when neither is active prevents wasted RAF cycles outside both
        // states.
        if (!broadcasting && !testingMix) {
            setAudioLevel(0);
            setVoiceLevel(0);
            return;
        }
        let rafId;
        const musicBuffer = new Uint8Array(128);
        const voiceBuffer = new Uint8Array(128);
        let lastSampleTs = 0;
        const sample = (ts) => {
            if (ts - lastSampleTs >= 33) {
                lastSampleTs = ts;
                // Music path — sampled when music mode is not 'off' AND an
                // analyser ref is set (set by startBroadcast for broadcast,
                // by startTestMix for test).
                const musicAnalyser = audioLevelAnalyserRef.current;
                if (musicAnalyser && musicMode !== 'off') {
                    musicAnalyser.getByteFrequencyData(musicBuffer);
                    let sum = 0;
                    for (let i = 0; i < musicBuffer.length; i++) sum += musicBuffer[i];
                    setAudioLevel(sum / musicBuffer.length / 255);
                } else {
                    setAudioLevel(0);
                }
                // Voice path — sampled whenever an analyser ref is set.
                const voiceAnalyser = voiceLevelAnalyserRef.current;
                if (voiceAnalyser) {
                    voiceAnalyser.getByteFrequencyData(voiceBuffer);
                    let sum = 0;
                    for (let i = 0; i < voiceBuffer.length; i++) sum += voiceBuffer[i];
                    setVoiceLevel(sum / voiceBuffer.length / 255);
                } else {
                    setVoiceLevel(0);
                }
            }
            rafId = requestAnimationFrame(sample);
        };
        rafId = requestAnimationFrame(sample);
        return () => cancelAnimationFrame(rafId);
    }, [broadcasting, musicMode, testingMix]);

    // ── Test Mix lifecycle (2026-04-25) ──
    // startTestMix — builds the SAME audio pipeline as startBroadcast (voice
    // + music chains, full leveler/makeup/limiter), but with no destination
    // node, no MediaRecorder, no WebSocket. Pure silent monitoring driving
    // the meters. Music source is acquired based on current mode:
    //   • computer: prompts getDisplayMedia (REQUIRES the click that called
    //               startTestMix to count as a user gesture — this is why
    //               we don't auto-start the test on mount)
    //   • file:     uses the already-selected musicFile via <audio> element
    //   • off:      no music source — voice meter only
    // Refs (voiceLevelAnalyserRef, audioLevelAnalyserRef) are temporarily
    // pointed at the test analysers; the cleanup closure does identity
    // checks before nulling them so a startBroadcast that overwrites them
    // mid-test (impossible flow but defensive) wouldn't be stomped.
    const startTestMix = useCallback(async () => {
        if (testingMix || broadcasting || connecting) return;
        if (!streamRef.current || !streamRef.current.active) {
            setError('Camera/microphone preview is not active. Wait a moment and try again.');
            return;
        }
        setError('');

        // Acquire music source per mode
        let displayStream = null;
        let musicEl = null;
        if (musicMode === 'computer') {
            try {
                displayStream = await navigator.mediaDevices.getDisplayMedia({
                    video: true, audio: true,
                });
            } catch (_) {
                // User cancelled the share dialog — silent abort
                return;
            }
            const audioTracks = displayStream.getAudioTracks();
            if (audioTracks.length === 0) {
                setError('No audio was shared for the test. Toggle "Share audio" in the dialog and try again.');
                displayStream.getTracks().forEach((t) => { try { t.stop(); } catch (_) { /* ignore */ } });
                return;
            }
            displayStream.getVideoTracks().forEach((t) => { try { t.stop(); } catch (_) { /* ignore */ } });
        } else if (musicMode === 'file') {
            if (!musicFile) {
                setError('Select a music file first to test the mix.');
                return;
            }
            musicEl = new Audio(musicFile);
            musicEl.loop = true;
            musicEl.crossOrigin = 'anonymous';
        }
        // 'off' mode: no music source acquired — voice meter only

        // Build silent monitoring audio graph (no destination)
        let testCtx;
        try {
            testCtx = new (window.AudioContext || window.webkitAudioContext)();
            if (testCtx.state === 'suspended') {
                try { await testCtx.resume(); } catch (_) { /* noop */ }
            }
        } catch (e) {
            setError('Could not initialize audio test. ' + (e?.message || ''));
            if (displayStream) displayStream.getTracks().forEach((t) => { try { t.stop(); } catch (_) { /* ignore */ } });
            return;
        }

        // Voice chain (mirror of startBroadcast voice path)
        const micSource = testCtx.createMediaStreamSource(streamRef.current);
        const voiceHighPass = createVoiceHighPass(testCtx);
        const voiceLeveler = createVoiceLeveler(testCtx);
        const voiceMakeup = testCtx.createGain();
        voiceMakeup.gain.value = MIX_CONFIG.voiceMakeup;
        const voiceLimiter = createPeakLimiter(testCtx);
        const voiceAnalyser = testCtx.createAnalyser();
        voiceAnalyser.fftSize = 256;
        voiceLimiter.connect(voiceAnalyser);
        // Capture refs so we can null them at teardown without stomping live values
        const priorVoiceAnalyser = voiceLevelAnalyserRef.current;
        voiceLevelAnalyserRef.current = voiceAnalyser;
        // Wire chain (no destination — silent)
        micSource
            .connect(voiceHighPass)
            .connect(voiceLeveler)
            .connect(voiceMakeup)
            .connect(voiceLimiter);

        // Music chain (only if a music source was acquired)
        let musicAnalyser = null;
        const priorMusicAnalyser = audioLevelAnalyserRef.current;
        if (musicEl || displayStream) {
            let musicSource;
            if (musicEl) {
                musicSource = testCtx.createMediaElementSource(musicEl);
            } else {
                const stream = new MediaStream(displayStream.getAudioTracks());
                musicSource = testCtx.createMediaStreamSource(stream);
            }
            const musicLeveler = createMusicLeveler(testCtx);
            const musicMakeup = testCtx.createGain();
            musicMakeup.gain.value = MIX_CONFIG.musicMakeup;
            const musicLimiter = createPeakLimiter(testCtx);
            musicAnalyser = testCtx.createAnalyser();
            musicAnalyser.fftSize = 256;
            musicLimiter.connect(musicAnalyser);
            audioLevelAnalyserRef.current = musicAnalyser;
            musicSource
                .connect(musicLeveler)
                .connect(musicMakeup)
                .connect(musicLimiter);
            // For file mode, kick off playback so the music actually flows through
            // the test graph. (For computer mode, the source is already live from
            // getDisplayMedia and plays automatically.)
            if (musicEl) {
                musicEl.play().catch(() => { /* autoplay blocked — meter stays flat */ });
            }
        }

        // Teardown closure — identity-checked so we don't null refs that may
        // have been swapped out by a startBroadcast invocation.
        // 2026-04-25: this closure NO LONGER stops displayStream tracks.
        // The displayStream's lifetime is now managed by preAcquiredDisplayStreamRef
        // so it can survive a test→broadcast handoff (avoiding double share-prompt).
        // It's stopped by stopTestMix(releaseShare:true) on manual stop / state
        // invalidation / unmount, or transferred to displayStreamRef.current by
        // startBroadcast when it claims the share.
        testCleanupRef.current = () => {
            if (musicEl) {
                try { musicEl.pause(); } catch (_) { /* ignore */ }
            }
            try { testCtx.close(); } catch (_) { /* ignore */ }
            if (voiceLevelAnalyserRef.current === voiceAnalyser) {
                voiceLevelAnalyserRef.current = priorVoiceAnalyser;
            }
            if (musicAnalyser && audioLevelAnalyserRef.current === musicAnalyser) {
                audioLevelAnalyserRef.current = priorMusicAnalyser;
            }
        };

        // For computer mode: stash the displayStream so a subsequent
        // Start Broadcasting can reuse it without re-prompting (2026-04-25).
        // Set AT THE END so any earlier failure path goes through normal
        // local-var cleanup (which stops the stream) without populating the ref.
        if (displayStream) {
            preAcquiredDisplayStreamRef.current = displayStream;
            // Safety: if the user clicks the browser's "Stop sharing" overlay
            // during the test, the audio track ends. Null out the preAcquired
            // ref so a subsequent Start Broadcasting falls through to a fresh
            // getDisplayMedia rather than trying to use the dead stream.
            const audioTracks = displayStream.getAudioTracks();
            if (audioTracks[0]) {
                audioTracks[0].addEventListener('ended', () => {
                    if (preAcquiredDisplayStreamRef.current === displayStream) {
                        preAcquiredDisplayStreamRef.current = null;
                    }
                }, { once: true });
            }
        }

        setTestingMix(true);
    }, [testingMix, broadcasting, connecting, musicMode, musicFile]);

    // stopTestMix(opts) — opts.releaseShare (default true) controls whether
    // the preAcquired display stream is also stopped. Manual stop, mode/file
    // change, camera/mic change, and unmount all release the share. Only
    // startBroadcast keeps the share alive so it can pick it up below
    // (avoiding a second screen-share prompt).
    const stopTestMix = useCallback((opts) => {
        const releaseShare = !opts || opts.releaseShare !== false;
        if (!testingMix && !testCleanupRef.current && !preAcquiredDisplayStreamRef.current) return;
        try { testCleanupRef.current?.(); } catch (_) { /* ignore */ }
        testCleanupRef.current = null;
        if (releaseShare && preAcquiredDisplayStreamRef.current) {
            try {
                preAcquiredDisplayStreamRef.current.getTracks().forEach((t) => {
                    try { t.stop(); } catch (_) { /* ignore */ }
                });
            } catch (_) { /* ignore */ }
            preAcquiredDisplayStreamRef.current = null;
        }
        setTestingMix(false);
    }, [testingMix]);

    // Auto-stop the test if any input changes invalidate the test graph
    // (camera/mic swap rebuilds the preview stream so the test's micSource
    // would be reading from a stopped track; mode/file change means the
    // test no longer represents what would actually broadcast). stopTestMix
    // early-returns when testingMix is already false, so re-runs of this
    // effect when testingMix flips are harmless no-ops.
    useEffect(() => {
        stopTestMix();
    }, [selectedCamera, selectedMic, musicMode, musicFile, stopTestMix]);

    // Update preview when camera/mic selection changes
    // Preview uses the pinned tier's resolution (or 4K for auto) so what admin
    // sees here is representative of what viewers will see when broadcasting starts.
    const prevCameraRef = useRef('');
    const prevMicRef = useRef('');
    const prevPinRef = useRef('auto');
    useEffect(() => {
        if (!selectedCamera || !selectedMic || broadcasting) return;
        if (selectedCamera === prevCameraRef.current && selectedMic === prevMicRef.current && tierPin === prevPinRef.current && streamRef.current) return;
        prevCameraRef.current = selectedCamera;
        prevMicRef.current = selectedMic;
        prevPinRef.current = tierPin;

        const previewTierKey = tierPin === 'auto' ? '4k' : tierPin;
        const previewTier = QUALITY_TIERS[previewTierKey];

        let cancelled = false;
        async function startPreview() {
            try {
                if (streamRef.current) {
                    streamRef.current.getTracks().forEach((t) => t.stop());
                }
                const stream = await navigator.mediaDevices.getUserMedia({
                    // aspectRatio: 16/9 (2026-04-23) — iPhone via Continuity Camera
                    // delivers the native 4:3 sensor output unless we explicitly
                    // request widescreen. Without this the preview (and captured
                    // recording) letterboxes in 16:9 players. USB webcams are
                    // already 16:9 native, so this is a no-op for them.
                    video: { deviceId: { exact: selectedCamera }, width: { ideal: previewTier.width }, height: { ideal: previewTier.height }, aspectRatio: { ideal: 16 / 9 }, frameRate: { ideal: 30 } },
                    audio: { deviceId: { exact: selectedMic }, echoCancellation: false, noiseSuppression: false, autoGainControl: false },
                });
                if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
                streamRef.current = stream;
                if (previewRef.current) {
                    previewRef.current.srcObject = stream;
                }
                // Initialize zoom for the fresh preview track (2026-04-24):
                // reads capability, pulls stored preference for this deviceId,
                // applies best-effort. No-op on cameras without zoom support.
                // Runs AFTER srcObject so the preview video shows the zoom
                // change live rather than flashing native-1× first.
                const previewTrack = stream.getVideoTracks()[0];
                if (previewTrack) {
                    initZoomForTrack(previewTrack, selectedCamera, false).catch(() => {});
                }
            } catch (e) {
                if (!cancelled) setError('Could not access camera or microphone.');
            }
        }
        startPreview();
        return () => { cancelled = true; };
    }, [selectedCamera, selectedMic, broadcasting, tierPin, initZoomForTrack]);

    // NOTE (2026-04-24 same-day audio revision): the prior "Music volume control
    // (live adjustment)" useEffect that read `musicVolume` state and wrote it to
    // `musicGainRef.current.gain.value` has been retired. The broadcast mix is now
    // fixed at MIX_CONFIG.musicBusGain (0.25) post-compressor — not admin-tunable
    // — because the slider was the source of the "system volume changes how loud
    // the music is in the broadcast" bug. Admin's own listening volume is decoupled
    // from the broadcast and can be adjusted on their speakers/headphones without
    // affecting viewers. See MIX_CONFIG at top of file for tuning rationale.

    // Handle music file selection
    const handleMusicFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setMusicFile(URL.createObjectURL(file));
        setMusicFileName(file.name);
    };

    // Keep broadcastingRef in sync so async WebSocket handlers know current state
    useEffect(() => { broadcastingRef.current = broadcasting; }, [broadcasting]);
    // Keep tier-related refs in sync with their state counterparts so async
    // relay-status handlers (which close over old state) read fresh values.
    useEffect(() => { activeTierRef.current = activeTier; }, [activeTier]);
    useEffect(() => { adjustingTierRef.current = adjustingTier; }, [adjustingTier]);
    useEffect(() => { tierPinRef.current = tierPin; }, [tierPin]);

    // Auto-dismiss tier-change toast after a few seconds — purely cosmetic, the
    // broadcast state itself is already updated.
    useEffect(() => {
        if (!tierNotice) return;
        const t = setTimeout(() => setTierNotice(''), 4000);
        return () => clearTimeout(t);
    }, [tierNotice]);

    // Elapsed timer
    useEffect(() => {
        if (broadcasting) {
            startTimeRef.current = Date.now();
            timerRef.current = setInterval(() => {
                setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
            }, 1000);
        } else {
            if (timerRef.current) clearInterval(timerRef.current);
            setElapsed(0);
        }
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }, [broadcasting]);

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    // Check if computer audio capture is supported
    const isComputerAudioSupported = typeof navigator.mediaDevices?.getDisplayMedia === 'function';

    // ── Adaptive quality helpers ──
    // Build a MediaRecorder for a given stream + tier. The ondataavailable handler
    // reads wsRef.current at send time so it keeps working across a downshift (where
    // we swap the recorder but keep the same WebSocket).
    const buildRecorder = useCallback((stream, tier) => {
        const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
            ? 'video/webm;codecs=vp8,opus'
            : MediaRecorder.isTypeSupported('video/webm;codecs=h264,opus')
                ? 'video/webm;codecs=h264,opus'
                : 'video/webm';
        if (!MediaRecorder.isTypeSupported(mimeType)) return null;
        const recorder = new MediaRecorder(stream, {
            mimeType,
            videoBitsPerSecond: tier.videoBps,
            audioBitsPerSecond: tier.audioBps,
        });
        recorder.ondataavailable = (e) => {
            if (e.data.size === 0) return;
            // Pre-recorded path (Mux Direct Upload, 2026-04-23 hardening):
            // write the chunk to IndexedDB via the recordingBuffer service
            // instead of an in-memory array. Updates the heartbeat refs so
            // the observability UI can render "last frame Xs ago". Reads
            // classTypeRef (not the closed-over classType prop) so a re-render
            // that changes classType between recorder-build and chunk-delivery
            // doesn't misroute. classId is read from classIdRef for the same
            // reason — ondataavailable's closure was fixed at recorder-build
            // time and the prop could have changed since then (it doesn't in
            // practice, but the ref makes the semantics explicit).
            if (classTypeRef.current === 'prerecorded') {
                const cid = classIdRef.current;
                lastChunkAtRef.current = Date.now();
                totalBytesCapturedRef.current += e.data.size;
                if (cid) {
                    // Fire-and-forget the append — the service's promise-chain
                    // preserves ordering; stopBroadcast awaits flushChunks
                    // before reading. A single rejection (quota exceeded,
                    // transaction abort) is surfaced via recorderErrorMsg so
                    // admin can stop and upload whatever's on disk.
                    bufferAppend(cid, e.data).catch((err) => {
                        // Only the FIRST failure writes the banner; subsequent
                        // failures in the same session are already represented.
                        setRecorderErrorMsg((prev) => prev || (err?.name === 'QuotaExceededError'
                            ? 'Your device is out of storage for recording. Please click End Class to save what we have.'
                            : `Unable to save a recording frame: ${err?.message || 'unknown error'}. Click End Class to save what we have.`));
                    });
                }
                return;
            }
            // Live path: stream the chunk to the relay. Reads wsRef.current at send
            // time (not closed-over) so a tier downshift that swaps the recorder
            // but keeps the WS continues to work.
            const ws = wsRef.current;
            if (ws && ws.readyState === WebSocket.OPEN) {
                ws.send(e.data);
            }
        };
        // ── recorder.onerror (2026-04-23) ──
        // MediaRecorder can stop itself with an internal error (encoder stall,
        // track removal, out-of-memory inside the native pipeline). The prior
        // implementation had NO handler for this; chunks simply stopped
        // arriving and wall-clock UI kept ticking. We surface it via
        // recorderErrorMsg, which the broadcaster UI renders as a warning
        // chip + inline CTA. We do NOT auto-stop — the admin decides (they
        // can click End Class to save what's captured, or cancel the session).
        recorder.onerror = (ev) => {
            const msg = ev?.error?.message || ev?.error?.name || 'MediaRecorder reported an error';
            console.error('MediaRecorder.onerror:', msg, ev);
            setRecorderErrorMsg((prev) => prev || `Recording error: ${msg}. Click End Class to save what we have.`);
        };
        return recorder;
    }, []);

    // Attempt to step one tier down the ladder without tearing down the WebSocket
    // or the audio mix. We only replace the camera video track + restart the
    // MediaRecorder at a lower bitrate. The relay's ffmpeg is tolerant of WebM
    // container-level resolution changes (we're already using +genpts+discardcorrupt
    // +igndts), so the RTMP feed to Mux continues without a re-connect.
    const attemptDownshift = useCallback(async () => {
        if (adjustingTierRef.current) return;
        const current = activeTierRef.current;
        const next = nextLowerTier(current);
        if (!next) return; // already at lowest tier — nothing to do
        adjustingTierRef.current = true;
        setAdjustingTier(true);
        const newTier = QUALITY_TIERS[next];
        try {
            // Video-only getUserMedia — we keep the existing audio pipeline alive
            // so mic/music continues uninterrupted during the swap.
            const newVideoStream = await navigator.mediaDevices.getUserMedia({
                // aspectRatio: 16/9 (2026-04-23) — see startBroadcast for rationale.
                // Keeps the downshifted video track 16:9 so a mid-session tier
                // change doesn't suddenly change the output aspect ratio.
                video: { deviceId: { exact: selectedCamera }, width: { ideal: newTier.width }, height: { ideal: newTier.height }, aspectRatio: { ideal: 16 / 9 }, frameRate: { ideal: 30 } },
                audio: false,
            });
            const newVideoTrack = newVideoStream.getVideoTracks()[0];

            // Flush & stop the old recorder — a fresh one takes over below.
            if (recorderRef.current && recorderRef.current.state !== 'inactive') {
                try { recorderRef.current.stop(); } catch (_) { /* ignore */ }
            }
            // Release the old (higher-res) camera constraint so the browser can
            // fully commit resources to the new tier.
            if (videoTrackRef.current) {
                try { videoTrackRef.current.stop(); } catch (_) { /* ignore */ }
            }

            const audioTracks = audioTracksRef.current || [];
            const newOutputStream = new MediaStream([newVideoTrack, ...audioTracks]);
            videoTrackRef.current = newVideoTrack;
            // ── Preserve zoom across tier downshift (2026-04-24) ──
            // A lower-tier getUserMedia returns a fresh track at native 1×.
            // preferCurrent=true re-applies the in-flight zoomValueRef so an
            // admin who set 0.5× before Start Recording doesn't suddenly see
            // the lens snap back to 1× mid-broadcast when the relay
            // auto-downshifts.
            initZoomForTrack(newVideoTrack, selectedCamera, true).catch(() => {});
            outputStreamRef.current = newOutputStream;
            if (previewRef.current) previewRef.current.srcObject = newOutputStream;

            const recorder = buildRecorder(newOutputStream, newTier);
            if (!recorder) throw new Error('Could not create recorder at lower tier');
            recorderRef.current = recorder;
            // 250ms timeslice keeps WebM chunk delivery steady — prevents
            // bursty 1s pulses from starving ffmpeg's stdin on the relay and
            // creating multi-second RTMP gaps that Mux fills with slate.
            recorder.start(250);

            activeTierRef.current = next;
            setActiveTier(next);
            lastTierChangeAtRef.current = Date.now();
            speedSamplesRef.current = [];
            setTierNotice(`Adjusted quality to ${newTier.label} for smoother delivery.`);
        } catch (e) {
            console.error('Quality downshift failed:', e);
            // If we couldn't establish the lower tier, the existing pipeline is
            // still running — don't blow up the broadcast. Admin sees no toast.
        } finally {
            adjustingTierRef.current = false;
            setAdjustingTier(false);
        }
    }, [selectedCamera, buildRecorder, initZoomForTrack]);

    // Push a relay status sample onto the rolling window and check downshift
    // triggers. Only acts in 'auto' mode — locked tiers bypass this entirely.
    const evaluateHealth = useCallback((fps, speed) => {
        if (tierPinRef.current !== 'auto') return;
        if (adjustingTierRef.current) return;
        const now = Date.now();
        if (now - lastTierChangeAtRef.current < DOWNSHIFT_COOLDOWN_MS) return;

        const samples = speedSamplesRef.current;
        samples.push({ speed, fps, t: now });
        while (samples.length > SPEED_SAMPLE_COUNT) samples.shift();

        // Critical-speed trigger — one bad sample is enough to act.
        if (typeof speed === 'number' && speed <= DOWNSHIFT_CRITICAL_SPEED) {
            attemptDownshift();
            return;
        }
        // Rolling-window triggers require a full window of samples.
        if (samples.length < SPEED_SAMPLE_COUNT) return;
        const slowCount = samples.filter((s) => typeof s.speed === 'number' && s.speed < DOWNSHIFT_SLOW_THRESHOLD).length;
        const lowFpsCount = samples.filter((s) => typeof s.fps === 'number' && s.fps < DOWNSHIFT_LOW_FPS).length;
        if (slowCount >= DOWNSHIFT_SLOW_MIN_COUNT || lowFpsCount >= DOWNSHIFT_SLOW_MIN_COUNT) {
            attemptDownshift();
        }
    }, [attemptDownshift]);

    // ── D-054: Auto-reconnect ──
    // When ws.onclose fires unexpectedly (relay crashed, network blip, Cloud Run
    // cold-start), we retry the WebSocket + MediaRecorder on exponential backoff
    // for up to 3 minutes. The camera stream stays alive throughout; we're just
    // re-establishing the wire to the relay. On success the broadcast resumes
    // on the existing Mux stream (Mux allows reconnect within its reconnect_window).
    //
    // Refs are used for cross-call coordination: `attemptReconnectRef` and
    // `beginReconnectRef` expose the latest functions to handlers (ws.onclose,
    // timeouts) without putting them in dep arrays.
    const attemptReconnectRef = useRef(null);
    const beginReconnectRef = useRef(null);

    const clearReconnectTimers = useCallback(() => {
        if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
        if (reconnectCountdownRef.current) { clearInterval(reconnectCountdownRef.current); reconnectCountdownRef.current = null; }
    }, []);

    const giveUpReconnect = useCallback((lastCloseCode) => {
        reconnectingRef.current = false;
        setReconnecting(false);
        clearReconnectTimers();
        reconnectAttemptsRef.current = 0;
        // Fall through to the original hard-error path.
        const msg = lastCloseCode === 1011
            ? 'Stream quality couldn\'t be sustained. Tap Try Again — we\'ll automatically adjust to a lower resolution if needed.'
            : 'Connection to the stream was interrupted. Tap Try Again to resume.';
        setError(msg);
        setBroadcasting(false);
        setConnecting(false);
    }, [clearReconnectTimers]);

    const attemptReconnect = useCallback((lastCloseCode) => {
        // Deadline check — abandon if we're past the 3-minute window.
        if (Date.now() >= reconnectDeadlineRef.current) {
            giveUpReconnect(lastCloseCode);
            return;
        }
        if (!broadcastingRef.current || adminStoppedRef.current) {
            // Admin stopped during backoff — no-op.
            reconnectingRef.current = false;
            setReconnecting(false);
            clearReconnectTimers();
            return;
        }

        // Re-open the WS to the relay with the same RTMP destination.
        try {
            const wsUrl = `${RELAY_URL}?rtmpUrl=${encodeURIComponent(rtmpUrl)}&streamKey=${encodeURIComponent(streamKey)}`;
            const ws = new WebSocket(wsUrl);
            wsRef.current = ws;

            const tier = QUALITY_TIERS[activeTierRef.current] || QUALITY_TIERS['4k'];
            const stream = outputStreamRef.current;

            ws.onopen = () => {
                console.log('Relay WebSocket re-opened (reconnect attempt ' + reconnectAttemptsRef.current + ')');
            };

            ws.onmessage = (event) => {
                try {
                    const msg = JSON.parse(event.data);
                    if (msg.type === 'connected') {
                        // Relay says Mux ingest is healthy. Rebuild recorder + resume.
                        if (!stream) {
                            // Very unlikely — outputStream was lost. Abandon.
                            giveUpReconnect(lastCloseCode);
                            return;
                        }
                        const recorder = buildRecorder(stream, tier);
                        if (!recorder) { giveUpReconnect(lastCloseCode); return; }
                        recorderRef.current = recorder;
                        // 250ms timeslice — see buildRecorder / startBroadcast for rationale.
                        recorder.start(250);
                        // Mark reconnect complete.
                        reconnectingRef.current = false;
                        setReconnecting(false);
                        clearReconnectTimers();
                        reconnectAttemptsRef.current = 0;
                        setError('');
                    } else if (msg.type === 'status') {
                        setEncodingStatus({ fps: msg.fps, speed: msg.speed });
                        evaluateHealth(msg.fps, msg.speed);
                    } else if (msg.type === 'error') {
                        setError(msg.message || 'Relay encoding error');
                    }
                } catch (e) { /* non-JSON */ }
            };

            ws.onerror = () => { /* onclose will follow — handle there */ };

            ws.onclose = (ev) => {
                // Still broadcasting = still reconnect-eligible. Schedule next attempt.
                if (broadcastingRef.current && !adminStoppedRef.current) {
                    beginReconnectRef.current?.(ev.code);
                }
            };
        } catch (e) {
            // Synchronous failure to open — retry via beginReconnect.
            beginReconnectRef.current?.(lastCloseCode);
        }
    }, [rtmpUrl, streamKey, buildRecorder, evaluateHealth, clearReconnectTimers, giveUpReconnect]);

    useEffect(() => { attemptReconnectRef.current = attemptReconnect; }, [attemptReconnect]);

    const beginReconnect = useCallback((lastCloseCode) => {
        // First-time entry into the reconnect loop — arm the deadline.
        if (!reconnectingRef.current) {
            reconnectingRef.current = true;
            setReconnecting(true);
            reconnectAttemptsRef.current = 0;
            reconnectDeadlineRef.current = Date.now() + RECONNECT_DEADLINE_MS;
            setReconnectRemainingMs(RECONNECT_DEADLINE_MS);

            // Update the "Xs remaining" countdown once per second.
            reconnectCountdownRef.current = setInterval(() => {
                const remaining = Math.max(0, reconnectDeadlineRef.current - Date.now());
                setReconnectRemainingMs(remaining);
                if (remaining <= 0) {
                    // Deadline hit — bail out on the next attempt tick.
                    clearInterval(reconnectCountdownRef.current);
                    reconnectCountdownRef.current = null;
                }
            }, 1000);
        }

        // Schedule next attempt on the backoff schedule.
        const attempt = reconnectAttemptsRef.current;
        const delay = RECONNECT_BACKOFF_MS[Math.min(attempt, RECONNECT_BACKOFF_MS.length - 1)];
        reconnectAttemptsRef.current = attempt + 1;

        // If the next attempt would land past the deadline, give up now.
        if (Date.now() + delay > reconnectDeadlineRef.current) {
            giveUpReconnect(lastCloseCode);
            return;
        }

        clearReconnectTimers();
        reconnectTimerRef.current = setTimeout(() => {
            attemptReconnectRef.current?.(lastCloseCode);
        }, delay);
    }, [clearReconnectTimers, giveUpReconnect]);

    useEffect(() => { beginReconnectRef.current = beginReconnect; }, [beginReconnect]);

    // ── START BROADCASTING ──
    const startBroadcast = useCallback(async () => {
        // Pre-recorded uses Mux Direct Upload — no RTMP relay, no stream key needed.
        // Live classes require both the relay URL and the Mux-issued stream key.
        if (classType !== 'prerecorded') {
            if (!RELAY_URL) {
                setError('Live relay server not configured. Set REACT_APP_LIVE_RELAY_URL.');
                return;
            }
            if (!rtmpUrl || !streamKey) {
                setError('Stream not created yet. Click "Prepare Stream" first.');
                return;
            }
        }

        // ── End any active Test Mix audio graph BEFORE building broadcast graph (2026-04-25) ──
        // Test Mix and broadcast both manipulate voiceLevelAnalyserRef +
        // audioLevelAnalyserRef and both create AudioContexts on streamRef.current.
        // We tear down the test's audio context here, BUT we intentionally do NOT
        // release the displayStream (preAcquiredDisplayStreamRef stays populated)
        // so the broadcast computer-mode branch below can pick it up and avoid
        // re-prompting the user for screen share. Manual stop / state changes
        // release the share via stopTestMix(); broadcast pickup is the only
        // path that preserves it.
        if (testCleanupRef.current) {
            try { testCleanupRef.current(); } catch (_) { /* ignore */ }
            testCleanupRef.current = null;
            setTestingMix(false);
        }

        setConnecting(true);
        setError('');
        // Clear any latched recorder-error banner from a prior session (live-parity
        // pass, 2026-04-24). Pre-recorded already clears this inside its branch
        // (see `setRecorderErrorMsg('')` at the heartbeat-reset block), but live
        // had no equivalent — a stale banner would have briefly flashed on the
        // next broadcast between setBroadcasting(true) and the first new chunk.
        setRecorderErrorMsg('');
        // D-054: new session — reset the admin-stop flag so an unexpected ws.onclose
        // during this session does get intercepted by the reconnect loop.
        adminStoppedRef.current = false;
        // D-053: fresh session — clear cue-sequence latches and sample counter so the
        // BroadcasterCueSequence starts from 'connecting' again. A reconnect within an
        // existing broadcast does NOT reset these (that path leaves the cue already
        // dismissed and we don't want to re-show it).
        wsOpenFiredRef.current = false;
        muxConnectedFiredRef.current = false;
        stabilizedFiredRef.current = false;
        goodSampleCountRef.current = 0;

        try {
            // Determine the starting tier. In 'auto' mode we begin at whatever
            // activeTierRef currently holds — normally that's '4k' (fresh session),
            // but if the previous session died with a 1011 ffmpeg-stdin error we
            // already stepped it down one tier, so Try Again won't immediately
            // collapse at the same level again. In a locked mode we respect the
            // admin's choice regardless of prior session outcome.
            const startingTierKey = tierPin === 'auto' ? (activeTierRef.current || '4k') : tierPin;
            const startingTier = QUALITY_TIERS[startingTierKey] || QUALITY_TIERS['4k'];

            // Get the camera + mic stream at the starting tier's resolution. If the
            // camera caps below the request (e.g. 1080p webcam asked for 4K), we
            // clamp bitrate down below to match the actual delivered resolution so
            // we don't waste bandwidth on empty pixels.
            const rawStream = await navigator.mediaDevices.getUserMedia({
                // aspectRatio: 16/9 (2026-04-23) — iPhone via Continuity Camera
                // delivers the native 4:3 sensor output (~1920x1440) unless we
                // explicitly request widescreen. Without this the recording is
                // letterboxed when viewed in our standard 16:9 Mux Player on the
                // viewer side. Native-16:9 cameras (Pocket 3 USB webcam mode,
                // Logitech/MacBook built-ins) treat this as a no-op.
                video: { deviceId: { exact: selectedCamera }, width: { ideal: startingTier.width }, height: { ideal: startingTier.height }, aspectRatio: { ideal: 16 / 9 }, frameRate: { ideal: 30 } },
                audio: { deviceId: { exact: selectedMic }, echoCancellation: false, noiseSuppression: false, autoGainControl: false },
            });

            let outputStream;

            if (musicMode === 'file' && musicFile) {
                // ── Mix mic + music FILE using podcast-standard broadcast chain ──
                // 2026-04-24 rev 2: replaced the prior heavy-compressor chain (which
                // sounded "chamber-y" on real-world test) with a podcast-production-
                // standard leveler + makeup + limiter chain. See MIX_CONFIG at top
                // of file for the full rationale + per-node explanation.
                const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
                audioCtxRef.current = audioCtx;
                // Chrome creates AudioContext in 'suspended' state when constructed
                // after an `await` has broken the user-gesture context. Without an
                // explicit resume(), nodes are wired but NO audio flows — broadcast
                // and recording would both be silent. No-op if already running.
                if (audioCtx.state === 'suspended') {
                    try { await audioCtx.resume(); } catch (_) { /* noop */ }
                }

                // ── Voice chain: highPass → leveler → makeup → limiter → bus → mix ──
                // The makeup gain (voiceMakeup) is what lets quiet USB lavaliers
                // sound as present as hot Bluetooth mics — it replicates in software
                // what hardware AGC does in a headset mic.
                const micSource = audioCtx.createMediaStreamSource(rawStream);
                const voiceHighPass = createVoiceHighPass(audioCtx);
                const voiceLeveler = createVoiceLeveler(audioCtx);
                const voiceMakeup = audioCtx.createGain();
                voiceMakeup.gain.value = MIX_CONFIG.voiceMakeup;
                const voiceLimiter = createPeakLimiter(audioCtx);
                const voiceGain = audioCtx.createGain();
                voiceGain.gain.value = MIX_CONFIG.voiceBusGain;

                // ── Music chain: leveler → makeup → limiter → bus → mix ──
                // No highPass on music — it would strip the bass frequencies that
                // carry the musical feel (kick drum, sub-bass). Music's leveler is
                // gentler (2:1) with slower attack (50ms) so transients survive.
                const musicEl = new Audio(musicFile);
                musicEl.loop = true;
                musicEl.crossOrigin = 'anonymous';
                musicElementRef.current = musicEl;
                const musicSource = audioCtx.createMediaElementSource(musicEl);
                musicSourceRef.current = musicSource;
                const musicLeveler = createMusicLeveler(audioCtx);
                const musicMakeup = audioCtx.createGain();
                musicMakeup.gain.value = MIX_CONFIG.musicMakeup;
                const musicLimiter = createPeakLimiter(audioCtx);
                const musicGain = audioCtx.createGain();
                musicGain.gain.value = MIX_CONFIG.musicBusGain;
                musicGainRef.current = musicGain;

                // ── Analyser taps — post-limiter on BOTH paths so the meters show
                //    actual broadcast levels (not raw input). Voice meter + music
                //    meter are both rendered in the in-broadcast audio panel. ──
                const voiceAnalyser = audioCtx.createAnalyser();
                voiceAnalyser.fftSize = 256;
                voiceLimiter.connect(voiceAnalyser);
                voiceLevelAnalyserRef.current = voiceAnalyser;

                const musicAnalyser = audioCtx.createAnalyser();
                musicAnalyser.fftSize = 256;
                musicLimiter.connect(musicAnalyser);
                audioLevelAnalyserRef.current = musicAnalyser;

                const mixDest = audioCtx.createMediaStreamDestination();
                // Chain .connect() returns the destination node, so these form graphs:
                //   VOICE: micSource → voiceHighPass → voiceLeveler → voiceMakeup
                //                   → voiceLimiter → voiceGain → mixDest
                //   MUSIC: musicSource → musicLeveler → musicMakeup
                //                     → musicLimiter → musicGain → mixDest
                micSource
                    .connect(voiceHighPass)
                    .connect(voiceLeveler)
                    .connect(voiceMakeup)
                    .connect(voiceLimiter)
                    .connect(voiceGain)
                    .connect(mixDest);
                musicSource
                    .connect(musicLeveler)
                    .connect(musicMakeup)
                    .connect(musicLimiter)
                    .connect(musicGain)
                    .connect(mixDest);

                musicEl.play().catch(() => { });
                setMusicPlaying(true);

                const videoTrack = rawStream.getVideoTracks()[0];
                outputStream = new MediaStream([videoTrack, ...mixDest.stream.getAudioTracks()]);

            } else if (musicMode === 'computer') {
                // ── Capture COMPUTER AUDIO ──
                // 2026-04-25: if a Test Mix click recently acquired a displayStream
                // and it's still active, REUSE it here instead of prompting the user
                // for screen share a second time. preAcquiredDisplayStreamRef is
                // populated by startTestMix and intentionally NOT cleared by the
                // top-of-startBroadcast test cleanup (only the test's audio context
                // is closed). If no usable preAcquired stream exists (admin skipped
                // Test Mix, or stopped it manually, or share went stale), we fall
                // through to a fresh getDisplayMedia prompt.
                let displayStream;
                if (preAcquiredDisplayStreamRef.current && preAcquiredDisplayStreamRef.current.active) {
                    displayStream = preAcquiredDisplayStreamRef.current;
                    preAcquiredDisplayStreamRef.current = null; // ownership transfers to broadcast
                } else {
                    // No reusable share — prompt for one. Stale ref (if any) is
                    // cleared so a future test attempt starts cleanly.
                    if (preAcquiredDisplayStreamRef.current) {
                        try {
                            preAcquiredDisplayStreamRef.current.getTracks().forEach((t) => {
                                try { t.stop(); } catch (_) { /* ignore */ }
                            });
                        } catch (_) { /* ignore */ }
                        preAcquiredDisplayStreamRef.current = null;
                    }
                    try {
                        displayStream = await navigator.mediaDevices.getDisplayMedia({
                            video: true, // required by the API, but we discard the video track
                            audio: true, // this captures system/tab audio
                        });
                    } catch (e) {
                        // User cancelled the screen share dialog
                        setError('Computer audio requires sharing a screen or tab with audio enabled. Please try again and check "Share audio."');
                        setConnecting(false);
                        rawStream.getTracks().forEach((t) => t.stop());
                        return;
                    }
                }

                // Check if audio was actually shared (defensive — for both
                // fresh-acquired and reused streams, a missing audio track is fatal).
                const displayAudioTracks = displayStream.getAudioTracks();
                if (displayAudioTracks.length === 0) {
                    setError('No audio was shared. Please try again — when the share dialog appears, check "Share audio" or "Share system audio."');
                    setConnecting(false);
                    displayStream.getTracks().forEach((t) => t.stop());
                    rawStream.getTracks().forEach((t) => t.stop());
                    return;
                }

                displayStreamRef.current = displayStream;

                // Stop the display video track — we only want the audio
                displayStream.getVideoTracks().forEach((t) => t.stop());

                // ── Mix mic + COMPUTER audio using podcast-standard broadcast chain ──
                // 2026-04-24 rev 2: same leveler + makeup + limiter chain as Music
                // File mode above. The prior rev 1's heavy 8:1 compressor was
                // replaced because it sounded "chamber-y" on real-world test.
                // Critical for this mode specifically: the makeup gain on music
                // + the slow leveler are what replace the old "system volume
                // scales broadcast" behavior with "system volume is smoothed
                // out over ~seconds". See MIX_CONFIG at top for full rationale.
                const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
                audioCtxRef.current = audioCtx;
                // Chrome/Edge create AudioContext in 'suspended' state when
                // constructed after an `await` has broken the user-gesture
                // context (getDisplayMedia above counts). Without an explicit
                // resume(), the mic + computer sources would be wired to mixDest
                // but NO audio actually flows — the broadcast and recording
                // would both be silent. No-op if already running.
                if (audioCtx.state === 'suspended') {
                    try { await audioCtx.resume(); } catch (_) { /* noop */ }
                }

                // ── Voice chain: highPass → leveler → makeup → limiter → bus → mix ──
                const micSource = audioCtx.createMediaStreamSource(rawStream);
                const voiceHighPass = createVoiceHighPass(audioCtx);
                const voiceLeveler = createVoiceLeveler(audioCtx);
                const voiceMakeup = audioCtx.createGain();
                voiceMakeup.gain.value = MIX_CONFIG.voiceMakeup;
                const voiceLimiter = createPeakLimiter(audioCtx);
                const voiceGain = audioCtx.createGain();
                voiceGain.gain.value = MIX_CONFIG.voiceBusGain;

                // ── Computer-audio chain: source → leveler → makeup → limiter → bus → mix ──
                const displayAudioStream = new MediaStream(displayAudioTracks);
                const computerSource = audioCtx.createMediaStreamSource(displayAudioStream);
                musicSourceRef.current = computerSource;
                const computerLeveler = createMusicLeveler(audioCtx);
                const computerMakeup = audioCtx.createGain();
                computerMakeup.gain.value = MIX_CONFIG.musicMakeup;
                const computerLimiter = createPeakLimiter(audioCtx);
                const computerGain = audioCtx.createGain();
                computerGain.gain.value = MIX_CONFIG.musicBusGain;
                musicGainRef.current = computerGain;

                // ── Analyser taps — post-limiter on both paths ──
                const voiceAnalyser = audioCtx.createAnalyser();
                voiceAnalyser.fftSize = 256;
                voiceLimiter.connect(voiceAnalyser);
                voiceLevelAnalyserRef.current = voiceAnalyser;

                const musicAnalyser = audioCtx.createAnalyser();
                musicAnalyser.fftSize = 256;
                computerLimiter.connect(musicAnalyser);
                audioLevelAnalyserRef.current = musicAnalyser;

                const mixDest = audioCtx.createMediaStreamDestination();
                //   VOICE:  micSource → voiceHighPass → voiceLeveler → voiceMakeup
                //                    → voiceLimiter → voiceGain → mixDest
                //   MUSIC:  computerSource → computerLeveler → computerMakeup
                //                         → computerLimiter → computerGain → mixDest
                micSource
                    .connect(voiceHighPass)
                    .connect(voiceLeveler)
                    .connect(voiceMakeup)
                    .connect(voiceLimiter)
                    .connect(voiceGain)
                    .connect(mixDest);
                computerSource
                    .connect(computerLeveler)
                    .connect(computerMakeup)
                    .connect(computerLimiter)
                    .connect(computerGain)
                    .connect(mixDest);

                setMusicPlaying(true);

                const videoTrack = rawStream.getVideoTracks()[0];
                outputStream = new MediaStream([videoTrack, ...mixDest.stream.getAudioTracks()]);

                // If the user stops screen sharing mid-broadcast, handle gracefully
                displayAudioTracks[0].onended = () => {
                    console.log('Computer audio sharing stopped by user');
                    setMusicPlaying(false);
                    // Don't stop the broadcast — just lose the music
                };

            } else {
                // No music — use raw camera + mic stream directly
                outputStream = rawStream;
            }

            streamRef.current = rawStream;

            // Determine actual delivered tier — the camera may deliver less than we
            // asked for (e.g. 1080p webcam returning 1920×1080 even when we requested
            // 4K). Set the effective tier to the highest ladder entry whose target
            // width is ≤ the camera's real videoWidth, but never above the starting
            // tier (i.e. if admin pinned 720p, don't auto-upshift to 1080p).
            const videoTrack = rawStream.getVideoTracks()[0];
            const trackSettings = videoTrack?.getSettings?.() || {};
            const actualWidth = trackSettings.width || startingTier.width;
            const effectiveTierKey = tierForActualWidth(actualWidth, startingTierKey);
            const effectiveTier = QUALITY_TIERS[effectiveTierKey];

            // Stash tracks + stream so attemptDownshift can swap only the video
            // track without tearing down the audio (mic/music) graph.
            videoTrackRef.current = videoTrack;
            // ── Re-apply zoom for the broadcast track (2026-04-24) ──
            // startBroadcast acquires a NEW video track via getUserMedia —
            // separate from the preview track, and a fresh getUserMedia call
            // always starts at the camera's native 1× regardless of what the
            // preview track was showing. Pull the stored preference for this
            // deviceId so 0.5×/0.75×/2× survives the preview→broadcast
            // transition. Fire-and-forget to avoid blocking the hot path into
            // the BroadcasterCueSequence handshake.
            if (videoTrack) {
                initZoomForTrack(videoTrack, selectedCamera, false).catch(() => {});
            }
            audioTracksRef.current = outputStream.getAudioTracks();
            outputStreamRef.current = outputStream;
            activeTierRef.current = effectiveTierKey;
            setActiveTier(effectiveTierKey);
            // Reset health samples + cooldown for a fresh session.
            speedSamplesRef.current = [];
            lastTierChangeAtRef.current = 0;

            // Show preview
            if (previewRef.current) {
                previewRef.current.srcObject = outputStream;
            }

            // ── Pre-recorded path (Mux Direct Upload) ──
            // No WebSocket, no relay, no RTMP. The admin records locally — chunks
            // are written to IndexedDB via the recordingBuffer service (2026-04-23
            // hardening; prior in-memory array could exhaust browser blob quota on
            // long recordings). On stop, stopBroadcast assembles the Blob from IDB
            // and hands it to the parent
            // (Workouts.js) which PUTs it to a Mux Direct Upload URL. This gives
            // Mux a pristine source file (vs. a re-transcoded RTMP intermediate)
            // and eliminates three classes of bug: stuttering slate flashes
            // mid-VOD, tail truncation on stop, and ffmpeg quality loss. See
            // api/mux-direct-upload.js for the endpoint + rationale.
            //
            // Cue sequence (BroadcasterCueSequence) is already skipped for
            // pre-recorded by the parent — no onWsOpen/onMuxConnected/onStabilized
            // calls are needed here. onBroadcastStart fires immediately (no relay
            // handshake to wait for).
            if (classType === 'prerecorded') {
                // ── classId required for IDB keying (2026-04-23) ──
                // Parent wires this from broadcastingClassId. Defensive: if a
                // caller ever forgot to pass it we refuse to start recording
                // rather than record into an untracked bucket that couldn't
                // be recovered. Existing live-class behavior is unaffected.
                if (!classId) {
                    setError('Cannot start recording: class identifier missing. Reload the page and try again.');
                    setConnecting(false);
                    // Release the just-acquired camera so the LED turns off.
                    try { rawStream.getTracks().forEach((t) => t.stop()); } catch (_) { /* ignore */ }
                    return;
                }

                // ── Storage pre-flight (2026-04-23) ──
                // Warn before we start if free storage is below the threshold.
                // We don't block — admin may still want to record a short class
                // even with low headroom — but the banner sets expectations and
                // the heartbeat will catch an eventual QuotaExceededError.
                try {
                    const est = await bufferEstimate();
                    if (est && est.freeBytes > 0 && est.freeBytes < STORAGE_WARN_BYTES) {
                        const freeGb = (est.freeBytes / (1024 * 1024 * 1024)).toFixed(1);
                        setRecorderErrorMsg(`Only ${freeGb} GB of browser storage available. Consider freeing space before recording long classes.`);
                    }
                } catch (_) { /* estimate is best-effort; fall through */ }

                const recorder = buildRecorder(outputStream, effectiveTier);
                if (!recorder) {
                    setError('Your browser does not support the required recording format.');
                    setConnecting(false);
                    return;
                }
                recorderRef.current = recorder;

                // ── IDB buffer start (2026-04-23) ──
                // Creates the metadata record and clears any residual chunks
                // from a prior aborted session on this classId. Synchronous
                // from the admin's perspective because the transaction commits
                // before we start the recorder — a later recovery prompt will
                // therefore see an accurate metadata entry even if the tab
                // dies on the next line.
                try {
                    await bufferStart(classId, recorder.mimeType || 'video/webm', {
                        classTitle: '', // title isn't a prop; parent can hydrate on recovery via classes lookup
                        videoBps: effectiveTier.videoBps,
                        audioBps: effectiveTier.audioBps,
                    });
                } catch (err) {
                    console.error('recordingBuffer.startRecording failed:', err);
                    setError('Unable to prepare local storage for recording. Close other tabs or free disk space and try again.');
                    setConnecting(false);
                    try { rawStream.getTracks().forEach((t) => t.stop()); } catch (_) { /* ignore */ }
                    return;
                }

                // Reset heartbeat refs so a fresh session starts from zero.
                lastChunkAtRef.current = Date.now();
                totalBytesCapturedRef.current = 0;
                setRecorderErrorMsg('');
                setHeartbeatTick(0);

                // ── Track .onended listeners (2026-04-23, revised same-day) ──
                // Originally these surfaced a red "Camera/Microphone disconnected"
                // banner on any track 'ended' event. Real-world test on the Osmo
                // Pocket 3 (USB-C webcam) revealed that some prosumer webcams
                // fire 'ended' on the MediaStreamTrack during normal operation
                // (mode switches, focus/exposure transitions, internal driver
                // re-handshake). Chrome re-acquires the device transparently and
                // chunks keep flowing — but the banner had already latched,
                // creating a false-positive that told the admin to end a
                // perfectly healthy recording.
                //
                // Revised behavior: log only. The AUTHORITATIVE signal for
                // "should admin end the class now?" is whether chunks are
                // actually arriving — already owned by the heartbeat panel
                // (HEARTBEAT_STALE_MS staleness detection). A real device
                // disconnect will stop chunks immediately, so the heartbeat
                // catches it within 2.5 s with an unambiguous message keyed
                // to actual chunk flow, not speculative track events.
                try {
                    const allTracks = outputStream.getTracks();
                    allTracks.forEach((track) => {
                        track.addEventListener('ended', () => {
                            if (!broadcastingRef.current) return;
                            const kind = track.kind === 'video' ? 'Camera' : 'Microphone';
                            console.warn(
                                `Track ended mid-recording (${kind}, kind=${track.kind}). ` +
                                `Heartbeat will detect real chunk stalls within ${HEARTBEAT_STALE_MS}ms.`
                            );
                        });
                    });
                } catch (_) { /* non-critical */ }

                // ── Screen Wake Lock request (2026-04-23) ──
                // Keeps the OS from dimming/sleeping/suspending this tab while
                // recording. Chromium + Safari 16.4+. On unsupported browsers
                // this throws; we swallow and the heartbeat + visibility-warning
                // pairing still catches the failure mode (just less preventively).
                try {
                    if (typeof navigator !== 'undefined' && navigator.wakeLock?.request) {
                        wakeLockRef.current = await navigator.wakeLock.request('screen');
                        // Browsers auto-release wake locks on visibility loss. We
                        // re-request when the tab returns (wired below, in the
                        // visibilitychange effect) so long recordings don't lose
                        // the lock after a quick tab-switch + return.
                        wakeLockRef.current.addEventListener?.('release', () => {
                            // Intentional no-op — the visibilitychange re-request
                            // handles reacquisition. Logging only for forensics.
                            console.log('Wake lock released');
                        });
                    }
                } catch (err) {
                    console.warn('Wake lock unavailable:', err?.message || err);
                }

                // 250ms timeslice — same cadence as the live path. For pre-recorded
                // this is less about steady delivery (no relay backpressure to worry
                // about) and more about bounded-memory chunk delivery: a 45-minute
                // recording at 4.5 Mbps is ~1.5 GB, delivered as ~11,000 chunks of
                // ~140 KB each. Each chunk goes straight to IDB — peak in-memory
                // blob footprint during recording is ONE chunk (~140 KB), not the
                // whole recording.
                recorder.start(250);
                broadcastingRef.current = true;
                setBroadcasting(true);
                setConnecting(false);
                setEncodingStatus(null);
                onBroadcastStart?.();
                return;
            }

            // ── Track .onended log-only listeners (live, 2026-04-24 live-parity pass) ──
            // Mirrors the pre-recorded block (see ~L1300). Forensic only — a real
            // camera/mic disconnect mid-broadcast manifests downstream as relay fps
            // dropping and the D-054 auto-reconnect flow kicking in; these listeners
            // just leave a console breadcrumb so post-incident forensics can tie
            // "broadcast went silent at 12:34" to "MediaStreamTrack ended at 12:34".
            // Critically NOT surfaced as a red banner: the Pocket 3 incident
            // (2026-04-23) showed some webcams fire 'ended' during normal mode
            // transitions while Chrome re-acquires transparently — a banner would
            // have been a false-positive instructing admin to end a healthy session.
            try {
                const allTracks = outputStream.getTracks();
                allTracks.forEach((track) => {
                    track.addEventListener('ended', () => {
                        if (!broadcastingRef.current) return;
                        const kind = track.kind === 'video' ? 'Camera' : 'Microphone';
                        console.warn(
                            `Track ended mid-broadcast (${kind}, kind=${track.kind}). ` +
                            `Relay fps drop + D-054 auto-reconnect will surface the impact downstream.`
                        );
                    });
                });
            } catch (_) { /* non-critical */ }

            // ── Connect WebSocket to relay server ──
            const wsUrl = `${RELAY_URL}?rtmpUrl=${encodeURIComponent(rtmpUrl)}&streamKey=${encodeURIComponent(streamKey)}`;
            const ws = new WebSocket(wsUrl);
            wsRef.current = ws;

            ws.onopen = () => {
                console.log('Relay WebSocket connected');
                // D-053 step 1→2: advance BroadcasterCueSequence from 'connecting' → 'stabilizing'.
                if (!wsOpenFiredRef.current) {
                    wsOpenFiredRef.current = true;
                    onWsOpenRef.current?.();
                }
            };

            ws.onmessage = (event) => {
                try {
                    const msg = JSON.parse(event.data);
                    if (msg.type === 'connected') {
                        const recorder = buildRecorder(outputStream, effectiveTier);
                        if (!recorder) {
                            setError('Your browser does not support the required recording format.');
                            setConnecting(false);
                            try { ws.close(); } catch (_) { /* ignore */ }
                            return;
                        }
                        recorderRef.current = recorder;
                        // 250ms timeslice: MediaRecorder delivers a WebM chunk every
                        // ~250ms instead of every 1s. Steady-cadence delivery keeps the
                        // relay's ffmpeg stdin fed continuously — preventing the
                        // multi-second input-starvation gaps that cause Mux to serve
                        // its slate placeholder image to viewers.
                        recorder.start(250);
                        setBroadcasting(true);
                        setConnecting(false);
                        setEncodingStatus(null);
                        onBroadcastStart?.();
                        // ── Acquire Screen Wake Lock (live, 2026-04-24 live-parity pass) ──
                        // Claim CLAUDE.md previously made: "WebSocket traffic keeps the
                        // tab active." True but incomplete — WS prevents full tab
                        // suspension, but (a) OS screen sleep is independent, (b)
                        // battery-saver / power-throttling on laptops can still
                        // affect MediaRecorder cadence, (c) Chromium throttles some
                        // APIs in hidden tabs even with active WS. Wake Lock closes
                        // these gaps cheaply. Released in stopBroadcast + unmount
                        // + auto-released by the browser on visibility-hidden (the
                        // visibilitychange effect below re-requests on return).
                        //
                        // Gated on `!wakeLockRef.current || released` so a D-054
                        // reconnect (which re-enters this block on each new WS
                        // 'connected') doesn't leak sentinels. Inline Promise chain
                        // because ws.onmessage is a non-async handler — factoring
                        // to an async helper would require changing its signature.
                        if (typeof navigator !== 'undefined' && navigator.wakeLock?.request &&
                            (!wakeLockRef.current || wakeLockRef.current.released)) {
                            navigator.wakeLock.request('screen').then((sentinel) => {
                                wakeLockRef.current = sentinel;
                                sentinel.addEventListener?.('release', () => {
                                    console.log('Wake lock released');
                                });
                            }).catch((err) => {
                                console.warn('Wake lock unavailable:', err?.message || err);
                            });
                        }
                        // D-053 step 3→4: relay reports RTMP → Mux handshake established.
                        // BroadcasterCueSequence advances 'verifying' → 'countdown'.
                        if (!muxConnectedFiredRef.current) {
                            muxConnectedFiredRef.current = true;
                            onMuxConnectedRef.current?.();
                        }
                    } else if (msg.type === 'status') {
                        // Encoding health from relay — fps and encoding speed
                        setEncodingStatus({ fps: msg.fps, speed: msg.speed });
                        // Feed the adaptive tier evaluator so we can downshift before
                        // ffmpeg's stdin buffer overflows into an EPIPE.
                        evaluateHealth(msg.fps, msg.speed);
                        // D-053 step 2→3: stabilization gate. After ~3 consecutive healthy
                        // samples (~3s at the relay's 1Hz cadence) we consider the pipeline
                        // stable enough to move past 'stabilizing'. A bad sample resets the
                        // counter so a flaky network doesn't prematurely advance the cue.
                        if (!stabilizedFiredRef.current) {
                            const healthy = (typeof msg.speed === 'number' && msg.speed >= 0.9)
                                && (typeof msg.fps === 'number' && msg.fps >= DOWNSHIFT_LOW_FPS);
                            if (healthy) {
                                goodSampleCountRef.current += 1;
                                if (goodSampleCountRef.current >= 3) {
                                    stabilizedFiredRef.current = true;
                                    onStabilizedRef.current?.();
                                }
                            } else {
                                goodSampleCountRef.current = 0;
                            }
                        }
                    } else if (msg.type === 'error') {
                        // Relay reported an encoding error
                        setError(msg.message || 'Relay encoding error');
                    }
                } catch (e) { /* non-JSON message, ignore */ }
            };

            ws.onerror = (e) => {
                console.error('Relay WebSocket error:', e);
                setError('Connection to relay server failed. Check your internet connection.');
                setConnecting(false);
            };

            ws.onclose = (e) => {
                console.log('Relay WebSocket closed:', e.code, e.reason);
                // If we're still broadcasting, this was an unexpected disconnect
                // (relay crashed, network dropped, etc.) — try to reconnect before
                // surfacing a hard error to admin (D-054).
                if (broadcastingRef.current && !adminStoppedRef.current) {
                    // Stop the local recorder immediately — it was feeding the dead WS.
                    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
                        try { recorderRef.current.stop(); } catch (_) { /* ignore */ }
                    }
                    recorderRef.current = null;
                    setEncodingStatus(null);
                    setAdjustingTier(false);
                    adjustingTierRef.current = false;

                    // If we hit an EPIPE at 4K, the retry should default to the
                    // next lower tier so we don't immediately collapse again.
                    if (e.code === 1011 && tierPinRef.current === 'auto') {
                        const nextKey = nextLowerTier(activeTierRef.current);
                        if (nextKey) {
                            activeTierRef.current = nextKey;
                            setActiveTier(nextKey);
                        }
                    }

                    // Kick off the auto-reconnect backoff schedule. If it exhausts
                    // the 3-minute deadline it will fall through to the old error
                    // path itself (see beginReconnect below).
                    beginReconnectRef.current?.(e.code);
                    return;
                }
            };

        } catch (e) {
            console.error('Broadcast start error:', e);
            setError(e.message || 'Failed to start broadcasting.');
            setConnecting(false);
        }
    }, [selectedCamera, selectedMic, musicMode, musicFile, rtmpUrl, streamKey, onBroadcastStart, tierPin, buildRecorder, evaluateHealth, classId, classType, initZoomForTrack]);

    // ── Wake Lock reacquire on tab return (2026-04-23, extended to live 2026-04-24) ──
    // Browsers auto-release screen wake locks when the tab is hidden. If admin
    // tabs away during a long broadcast and returns, we re-request the lock so
    // subsequent minutes stay protected. Covers BOTH class types: pre-recorded
    // acquires in startBroadcast (IDB path), live acquires in ws.onmessage
    // 'connected' (live-parity pass added 2026-04-24). Guarded on `broadcasting`
    // so we don't attach the listener outside active sessions. No-op on
    // browsers without the Wake Lock API.
    useEffect(() => {
        if (!broadcasting) return;
        if (typeof navigator === 'undefined' || !navigator.wakeLock?.request) return;
        const onVisible = async () => {
            if (document.visibilityState !== 'visible') return;
            // Don't double-acquire if the old lock is still live.
            if (wakeLockRef.current && !wakeLockRef.current.released) return;
            try {
                wakeLockRef.current = await navigator.wakeLock.request('screen');
            } catch (_) { /* best effort */ }
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [broadcasting]);

    // ── Heartbeat tick (pre-recorded, 2026-04-23) ──
    // Drives the "Captured / Last frame" UI row. Ticks once/second while
    // broadcasting; the UI reads lastChunkAtRef + totalBytesCapturedRef
    // directly to avoid a re-render every 250 ms (which chunk arrival
    // would otherwise cause). Stopped when broadcasting flips false.
    useEffect(() => {
        if (classTypeRef.current !== 'prerecorded') return;
        if (!broadcasting) return;
        const id = setInterval(() => {
            setHeartbeatTick((t) => (t + 1) % 1_000_000);
        }, 1000);
        return () => clearInterval(id);
    }, [broadcasting]);

    // ── Scroll-to-preview on recording start (pre-recorded, 2026-04-23) ──
    // When admin clicks Start Recording they may have scrolled down to adjust
    // camera/mic/music settings below the preview. Once recording begins, scroll
    // the preview video into view so they can immediately confirm the red
    // BROADCASTING chip is live and verify their framing. Scoped to pre-recorded
    // only — live classes already have the full-screen BroadcasterCueSequence
    // overlay commanding attention, and scroll-stealing mid-cue would be worse
    // than the status quo. The -80 offset matches the pattern Workouts.js uses
    // when scrolling to the broadcaster panel; it clears the desktop top nav
    // and leaves a bit of breathing room above the video frame.
    useEffect(() => {
        if (classTypeRef.current !== 'prerecorded') return;
        if (!broadcasting) return;
        const el = previewRef.current;
        if (!el || typeof el.getBoundingClientRect !== 'function') return;
        const y = el.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    }, [broadcasting]);

    // ── STOP BROADCASTING ──
    const [finishing, setFinishing] = useState(false);

    const stopBroadcast = useCallback(() => {
        // ── Pre-recorded path (Mux Direct Upload) ──
        // Stop the recorder, wait for the final ondataavailable chunk to flush
        // into IndexedDB, then assemble one composite Blob from the buffered
        // chunks and hand it to the parent. No WebSocket to close, no 5s relay
        // flush window, no camera-light race. MediaRecorder.stop() fires its
        // 'stop' event AFTER the final ondataavailable — so by the time we
        // build the Blob we have ALL captured content, including
        // content captured between click and MediaRecorder.stop() returning.
        //
        // Critically: unlike the live path, we do NOT call `/api/live-class
        // ?action=complete` anywhere in the pre-recorded flow. There's no live
        // stream to complete — the upload will be created fresh when the parent
        // calls `/api/mux-direct-upload`. The prior seal-before-stop ordering was
        // a necessary kludge for live-ingest; Direct Upload makes it obsolete.
        if (classTypeRef.current === 'prerecorded') {
            broadcastingRef.current = false;
            const recorder = recorderRef.current;
            recorderRef.current = null;
            const cidAtStop = classIdRef.current;

            // ── Release Wake Lock (pre-recorded, 2026-04-23) ──
            // Do this early — we want the OS to resume normal power behavior
            // regardless of whether IDB reads succeed below. Safe to call on a
            // released lock (no-op). Cleared to null so the visibilitychange
            // effect doesn't try to refresh a dead sentinel.
            if (wakeLockRef.current) {
                try { wakeLockRef.current.release?.(); } catch (_) { /* ignore */ }
                wakeLockRef.current = null;
            }

            // Shared teardown: camera/mic + Web Audio graph cleanup. Same work as
            // the live path below, duplicated here so we can run it AFTER the
            // Blob is assembled but BEFORE onBroadcastEnd fires. If the recorder
            // is already inactive (edge case — stop called twice), we still do
            // teardown so the camera LED turns off.
            //
            // Now async (2026-04-23) — reads the assembled Blob from IDB after
            // flushing pending chunk writes. The outer stopBroadcast caller does
            // not await the returned Promise (preserves the existing fire-and-
            // -forget caller contract); we just make sure any internal ordering
            // we need is preserved by awaiting inside.
            const finalizeAndTeardown = async () => {
                let blob = null;
                let mimeType = (recorder && recorder.mimeType) || 'video/webm';

                // Read the composite Blob from IDB. flushChunks waits for every
                // in-flight appendChunk to resolve first, so the last chunk
                // (delivered by MediaRecorder's final ondataavailable) is on
                // disk before we assemble. Errors here are survivable — we
                // still run media teardown, and the recovery path on next
                // mount will detect the unuploaded metadata record.
                if (cidAtStop) {
                    try {
                        await bufferFlush(cidAtStop);
                        const result = await bufferGetBlob(cidAtStop);
                        if (result.blob && result.blob.size > 0) {
                            blob = result.blob;
                            mimeType = result.mimeType || mimeType;
                        }
                    } catch (e) {
                        console.error('recordingBuffer.getRecordingBlob failed:', e);
                    }
                }

                // Media teardown — camera light off, audio graph closed, preview
                // cleared. Mirrors the live stopBroadcast teardown verbatim.
                if (musicElementRef.current) {
                    musicElementRef.current.pause();
                    musicElementRef.current = null;
                }
                if (displayStreamRef.current) {
                    displayStreamRef.current.getTracks().forEach((t) => t.stop());
                    displayStreamRef.current = null;
                }
                if (audioCtxRef.current) {
                    audioCtxRef.current.close().catch(() => { });
                    audioCtxRef.current = null;
                }
                audioLevelAnalyserRef.current = null;
                if (streamRef.current) {
                    streamRef.current.getTracks().forEach((t) => t.stop());
                    streamRef.current = null;
                }
                if (videoTrackRef.current) {
                    try { videoTrackRef.current.stop(); } catch (_) { /* ignore */ }
                    videoTrackRef.current = null;
                }
                audioTracksRef.current = [];
                outputStreamRef.current = null;
                setBroadcasting(false);
                setConnecting(false);
                setMusicPlaying(false);
                setEncodingStatus(null);
                setRecorderErrorMsg('');

                // Hand the Blob to the parent for upload to Mux. Fires BEFORE
                // onBroadcastEnd so the parent can latch upload state before
                // any broadcaster-unmount effect runs.
                //
                // IDB cleanup is the parent's responsibility: on successful
                // upload Workouts.js calls bufferClear(classId) via the
                // recordingBuffer service. Keeping the cleanup there means
                // a failed upload can retry from the same IDB data without
                // re-recording (covered by the existing Retry button in the
                // upload banner — it caches the Blob, but IDB is the true
                // source-of-truth across tab reloads).
                if (blob && onRecordingCompleteRef.current) {
                    try { onRecordingCompleteRef.current(blob, mimeType); } catch (e) { console.error('onRecordingComplete handler threw:', e); }
                } else if (!blob) {
                    console.warn('Pre-recorded session produced no chunks — nothing to upload');
                }
                onBroadcastEnd?.();
            };

            if (recorder && recorder.state !== 'inactive') {
                // Attach a one-shot stop listener BEFORE calling stop(). The 'stop'
                // event fires after the final ondataavailable, guaranteeing every
                // appendChunk has been enqueued in the IDB write chain; the async
                // finalize then awaits flushChunks before assembling.
                recorder.addEventListener('stop', () => { finalizeAndTeardown(); }, { once: true });
                try { recorder.stop(); } catch (e) {
                    console.error('MediaRecorder.stop threw:', e);
                    // Fallback: run teardown inline if stop() threw (rare — e.g. recorder
                    // already in an error state). We still emit whatever chunks we have.
                    finalizeAndTeardown();
                }
            } else {
                // Already inactive — no more chunks will arrive. Teardown now.
                finalizeAndTeardown();
            }
            return;
        }

        // ── Live path (WebSocket relay → RTMP → Mux) ──
        // Flip the broadcasting ref synchronously BEFORE any cleanup work. Without this
        // there's a race: the 5s graceful-close window below keeps the WebSocket open
        // while the relay's ffmpeg pipeline flushes, but Mux may drop RTMP in the
        // meantime and the relay's ws.onclose handler will fire with
        // `broadcastingRef.current === true` (the React state→ref sync effect hasn't
        // committed yet) — which shows the "Connection to the stream was interrupted"
        // error on what is actually an intentional, graceful shutdown.
        broadcastingRef.current = false;
        // D-054: tell the ws.onclose path this is an admin-initiated shutdown so it
        // does NOT launch the auto-reconnect backoff. Also halt any in-flight
        // reconnect attempt from a previous disconnect.
        adminStoppedRef.current = true;
        reconnectingRef.current = false;
        setReconnecting(false);
        if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
        if (reconnectCountdownRef.current) { clearInterval(reconnectCountdownRef.current); reconnectCountdownRef.current = null; }
        reconnectAttemptsRef.current = 0;

        // ── Release Wake Lock (live, 2026-04-24 live-parity pass) ──
        // Mirrors the pre-recorded release above at L1573. Release early so the
        // OS can resume normal power behavior regardless of the 5s WebSocket
        // graceful-close window below. Safe to call on a released lock (no-op).
        // Cleared to null so the visibilitychange re-request effect doesn't try
        // to refresh a dead sentinel.
        if (wakeLockRef.current) {
            try { wakeLockRef.current.release?.(); } catch (_) { /* ignore */ }
            wakeLockRef.current = null;
        }

        // Stop the recorder — triggers one final async ondataavailable with remaining data
        if (recorderRef.current && recorderRef.current.state !== 'inactive') {
            recorderRef.current.stop();
        }
        recorderRef.current = null;

        // Stop music and media tracks immediately (camera light off, music stops)
        if (musicElementRef.current) {
            musicElementRef.current.pause();
            musicElementRef.current = null;
        }
        if (displayStreamRef.current) {
            displayStreamRef.current.getTracks().forEach((t) => t.stop());
            displayStreamRef.current = null;
        }
        if (audioCtxRef.current) {
            audioCtxRef.current.close().catch(() => { });
            audioCtxRef.current = null;
        }
        audioLevelAnalyserRef.current = null;
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
        // After a downshift, videoTrackRef points at a fresh track NOT inside
        // streamRef — stop it separately so the camera LED actually turns off.
        if (videoTrackRef.current) {
            try { videoTrackRef.current.stop(); } catch (_) { /* ignore */ }
            videoTrackRef.current = null;
        }
        audioTracksRef.current = [];
        outputStreamRef.current = null;
        speedSamplesRef.current = [];
        adjustingTierRef.current = false;
        // D-053: reset cue-sequence latches so the next broadcast session starts clean.
        wsOpenFiredRef.current = false;
        muxConnectedFiredRef.current = false;
        stabilizedFiredRef.current = false;
        goodSampleCountRef.current = 0;

        // Update UI immediately — user sees broadcast has stopped
        setBroadcasting(false);
        setConnecting(false);
        setMusicPlaying(false);
        setEncodingStatus(null);
        setAdjustingTier(false);
        setTierNotice('');
        setFinishing(true);
        // Normal end — next broadcast should start fresh from the top of the ladder
        // (or from the admin's chosen pin). A 1011 crash does NOT go through here;
        // it goes through ws.onclose, which leaves activeTierRef one tier lower so
        // the retry doesn't immediately re-collapse at the same level.
        activeTierRef.current = '4k';
        setActiveTier('4k');

        // Keep the WebSocket open briefly so the relay can flush its transcoding
        // pipeline (WebM → RTMP → Mux). Without this delay, the relay's ffmpeg
        // buffer may not fully flush, causing the video to be cut short.
        const ws = wsRef.current;
        wsRef.current = null;

        setTimeout(() => {
            if (ws) {
                try { ws.close(); } catch (e) { /* already closed */ }
            }
            setFinishing(false);
            onBroadcastEnd?.();
        }, 5000);
    }, [onBroadcastEnd]);

    // ── End Class graceful shutdown ──
    // Parent (Workouts.js) writes `endingAt` on the class doc when admin clicks End Class
    // and mirrors it into the activeClass state. We observe the prop and trigger the same
    // graceful `stopBroadcast` path the admin would get from clicking the in-component
    // Stop Broadcast button — 5s pipeline flush, clean WebSocket close, no "Connection
    // interrupted" error on what is an intentional shutdown. Guarded by `broadcastingRef`
    // so we don't re-trigger if the effect runs while we're already tearing down (or if
    // admin clicks End Class when not actively broadcasting).
    //
    // Held in a ref to keep this effect's dep array minimal — we only want it to fire on
    // endingAt transitions, not on every re-render that recreates stopBroadcast.
    const stopBroadcastRef = useRef(null);
    useEffect(() => { stopBroadcastRef.current = stopBroadcast; }, [stopBroadcast]);
    useEffect(() => {
        if (!endingAt) return;
        if (!broadcastingRef.current) return;
        stopBroadcastRef.current?.();
    }, [endingAt]);

    // Cleanup on unmount only
    //
    // 2026-04-23 note: for pre-recorded classes, chunks captured up to the
    // moment of unmount are safely in IndexedDB via the recordingBuffer service.
    // Calling recorder.stop() here DOES fire one last ondataavailable → IDB
    // append; the metadata record survives too. On next mount, Workouts.js scans
    // for unfinished recordings and prompts the admin to upload or discard.
    // That gives us clean recovery from accidental tab navigation, error
    // boundaries, or any other path that unmounts the broadcaster mid-recording.
    useEffect(() => {
        return () => {
            if (recorderRef.current && recorderRef.current.state !== 'inactive') {
                recorderRef.current.stop();
            }
            if (wsRef.current) {
                wsRef.current.close();
            }
            if (musicElementRef.current) {
                musicElementRef.current.pause();
            }
            if (displayStreamRef.current) {
                displayStreamRef.current.getTracks().forEach((t) => t.stop());
            }
            if (audioCtxRef.current) {
                audioCtxRef.current.close().catch(() => { });
            }
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((t) => t.stop());
            }
            // Post-downshift: the active video track lives in videoTrackRef, not
            // streamRef — stop it explicitly to release the camera cleanly.
            if (videoTrackRef.current) {
                try { videoTrackRef.current.stop(); } catch (_) { /* ignore */ }
            }
            // Release the screen wake lock (pre-recorded, 2026-04-23). Browsers
            // release automatically on tab close, but if the component unmounts
            // while the tab stays open we'd hold a zombie sentinel until GC.
            if (wakeLockRef.current) {
                try { wakeLockRef.current.release?.(); } catch (_) { /* ignore */ }
                wakeLockRef.current = null;
            }
            // Tear down any active Test Mix audio graph (2026-04-25). The
            // test cleanup closure handles audio-context + music-element teardown.
            if (testCleanupRef.current) {
                try { testCleanupRef.current(); } catch (_) { /* ignore */ }
                testCleanupRef.current = null;
            }
            // Release the pre-acquired display stream too if one is still held —
            // unmount means there's no upcoming startBroadcast to claim it.
            if (preAcquiredDisplayStreamRef.current) {
                try {
                    preAcquiredDisplayStreamRef.current.getTracks().forEach((t) => {
                        try { t.stop(); } catch (_) { /* ignore */ }
                    });
                } catch (_) { /* ignore */ }
                preAcquiredDisplayStreamRef.current = null;
            }
        };
    }, []);

    if (!devicesLoaded) {
        return (
            <div style={{ padding: '24px', textAlign: 'center' }}>
                <div style={{ width: 24, height: 24, borderRadius: '50%', border: '3px solid var(--mom-cream-dark)', borderTopColor: 'var(--mom-primary)', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-warm-gray)' }}>Loading camera and microphone...</p>
            </div>
        );
    }

    // Can we start? Check based on music mode
    const canStart = selectedCamera && selectedMic && !connecting &&
        (musicMode === 'off' || musicMode === 'computer' || (musicMode === 'file' && musicFile));

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* ── Camera Preview ── */}
            <div style={{
                position: 'relative', width: '100%', paddingBottom: '56.25%',
                background: 'linear-gradient(135deg, var(--mom-charcoal), var(--mom-charcoal-light))',
                borderRadius: 16, overflow: 'hidden',
            }}>
                <video ref={previewRef} autoPlay muted playsInline
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }} />
                {broadcasting && (
                    <div style={{
                        position: 'absolute', top: 12, left: 12, right: 12,
                        display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            background: 'rgba(220, 38, 38, 0.9)', padding: '6px 14px', borderRadius: 20,
                        }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff', animation: 'livePulse 1.5s ease-in-out infinite' }} />
                            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 700, color: '#fff', letterSpacing: '0.06em' }}>
                                BROADCASTING · {formatTime(elapsed)}
                            </span>
                        </div>
                        {/* Encoding health indicator — shows relay is actively processing.
                            Null-safe formatting: a relay status message with a missing fps/speed
                            field must NEVER crash admin (Sentry ErrorBoundary kicks them to the
                            "Something went wrong" page mid-broadcast, which is catastrophic).
                            Outer guard only checks the object; inner values must be validated. */}
                        {encodingStatus && (() => {
                            const fpsNum = typeof encodingStatus.fps === 'number' && Number.isFinite(encodingStatus.fps) ? encodingStatus.fps : null;
                            const speedNum = typeof encodingStatus.speed === 'number' && Number.isFinite(encodingStatus.speed) ? encodingStatus.speed : null;
                            const bg = speedNum === null
                                ? 'rgba(0, 0, 0, 0.55)'
                                : speedNum >= 0.9
                                    ? 'rgba(22, 163, 74, 0.9)'
                                    : speedNum >= 0.5
                                        ? 'rgba(234, 179, 8, 0.9)'
                                        : 'rgba(220, 38, 38, 0.9)';
                            return (
                                <div style={{
                                    display: 'flex', alignItems: 'center', gap: 6,
                                    background: bg,
                                    padding: '6px 12px', borderRadius: 20,
                                }}>
                                    <div style={{
                                        width: 6, height: 6, borderRadius: '50%',
                                        background: '#fff',
                                    }} />
                                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 600, color: '#fff', letterSpacing: '0.04em' }}>
                                        {fpsNum === null ? '—' : Math.round(fpsNum)}fps · {speedNum === null ? '—' : speedNum.toFixed(1)}x
                                    </span>
                                </div>
                            );
                        })()}
                        {/* Active tier label — honest readout of the resolution currently
                            going to the relay. Updates instantly on auto-downshift. */}
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            background: adjustingTier ? 'rgba(234, 179, 8, 0.9)' : 'rgba(0, 0, 0, 0.55)',
                            padding: '6px 12px', borderRadius: 20,
                            backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                        }}>
                            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 600, color: '#fff', letterSpacing: '0.04em' }}>
                                {adjustingTier ? 'Adjusting quality…' : QUALITY_TIERS[activeTier]?.label || activeTier}
                            </span>
                        </div>
                    </div>
                )}
                {!selectedCamera && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'rgba(255,255,255,0.5)' }}>Select a camera to preview</p>
                    </div>
                )}
            </div>

            {/* ── Camera zoom picker (2026-04-24) ──
                Rendered only when the active camera exposes a `zoom`
                capability via getCapabilities(). iPhone via Continuity
                Camera reports this (min typically ~0.5, max ~5+); USB
                webcams and MacBook FaceTime do not. Values outside the
                camera's reported range render as disabled chips — NOT
                hidden — so the option surface is visually consistent
                across cameras. Sits outside the `{!broadcasting}` gate
                so the admin can reframe mid-recording; applyConstraints
                is in-place on the existing track, no stream rebuild,
                no MediaRecorder interruption. */}
            {zoomCapability && (
                <div>
                    <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: 'var(--mom-charcoal)', display: 'block', marginBottom: 6 }}>
                        Camera zoom
                    </label>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {ZOOM_OPTIONS.map((v) => {
                            const inRange = v >= zoomCapability.min && v <= zoomCapability.max;
                            const isActive = Math.abs(zoomValue - v) < 0.001;
                            return (
                                <button
                                    key={v}
                                    type="button"
                                    onClick={() => onZoomChipClick(v)}
                                    disabled={!inRange}
                                    title={inRange ? `Set camera zoom to ${v}×` : `${v}× is outside this camera's range`}
                                    style={{
                                        padding: '6px 14px', borderRadius: 10,
                                        fontSize: 12, fontWeight: 600,
                                        fontFamily: 'var(--mom-font-sans)',
                                        border: 'none',
                                        cursor: inRange ? 'pointer' : 'not-allowed',
                                        background: isActive ? 'var(--mom-primary)' : 'var(--mom-cream-dark)',
                                        color: isActive ? 'var(--mom-white)' : 'var(--mom-charcoal)',
                                        opacity: inRange ? 1 : 0.4,
                                        transition: 'all 0.2s',
                                    }}>
                                    {v}×
                                </button>
                            );
                        })}
                    </div>
                    {!broadcasting && (
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: 'var(--mom-warm-gray)', marginTop: 6, marginBottom: 0, lineHeight: 1.4 }}>
                            0.5× uses the iPhone's ultra-wide lens — fits more of the room in frame.
                        </p>
                    )}
                </div>
            )}

            {/* ── Device Selection ── */}
            {!broadcasting && (
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 200px' }}>
                        {/* Camera label row — label on left, Refresh button on right.
                            Refresh triggers an on-demand enumerateDevices() for
                            cases where the automatic `devicechange` listener
                            didn't surface a newly-connected device (iPhone via
                            Continuity Camera, hot-plugged USB webcam, etc.). */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                            <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: 'var(--mom-charcoal)' }}>Camera</label>
                            <button
                                type="button"
                                onClick={refreshDevices}
                                disabled={refreshingDevices}
                                title="Refresh the camera list (use this if you just connected an iPhone via Continuity Camera)"
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 4,
                                    padding: '2px 8px', borderRadius: 6,
                                    fontSize: 11, fontWeight: 600,
                                    fontFamily: 'var(--mom-font-sans)',
                                    color: 'var(--mom-primary)',
                                    background: 'transparent',
                                    border: '1px solid color-mix(in srgb, var(--mom-primary) 35%, transparent)',
                                    cursor: refreshingDevices ? 'wait' : 'pointer',
                                    opacity: refreshingDevices ? 0.6 : 1,
                                }}>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: refreshingDevices ? 'spin 0.8s linear infinite' : 'none' }}>
                                    <polyline points="23 4 23 10 17 10" />
                                    <polyline points="1 20 1 14 7 14" />
                                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                                </svg>
                                Refresh
                            </button>
                        </div>
                        <select className="mom-input" value={selectedCamera} onChange={(e) => setSelectedCamera(e.target.value)}
                            style={{ fontSize: 13, cursor: 'pointer' }}>
                            {cameras.map((c) => (
                                <option key={c.deviceId} value={c.deviceId}>{c.label || `Camera ${cameras.indexOf(c) + 1}`}</option>
                            ))}
                        </select>
                    </div>
                    <div style={{ flex: '1 1 200px' }}>
                        <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: 'var(--mom-charcoal)', marginBottom: 4, display: 'block' }}>Microphone</label>
                        <select className="mom-input" value={selectedMic} onChange={(e) => setSelectedMic(e.target.value)}
                            style={{ fontSize: 13, cursor: 'pointer' }}>
                            {mics.map((m) => (
                                <option key={m.deviceId} value={m.deviceId}>{m.label || `Mic ${mics.indexOf(m) + 1}`}</option>
                            ))}
                        </select>
                    </div>
                    {/* Quality tier pin — 'auto' (default) lets us start at 4K and
                        step down if the relay can't keep up. A locked tier skips
                        auto-adjustment entirely and holds the broadcast at that
                        fixed resolution. Stays hidden during broadcast; tier can't
                        be changed mid-broadcast. */}
                    <div style={{ flex: '1 1 200px' }}>
                        <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: 'var(--mom-charcoal)', marginBottom: 4, display: 'block' }}>Broadcast Quality</label>
                        <select className="mom-input" value={tierPin} onChange={(e) => setTierPin(e.target.value)}
                            style={{ fontSize: 13, cursor: 'pointer' }}>
                            <option value="auto">Auto (start at 4K, adjust as needed)</option>
                            <option value="4k">4K (locked)</option>
                            <option value="1080">1080p (locked)</option>
                            <option value="720">720p (locked)</option>
                        </select>
                    </div>
                </div>
            )}

            {/* ── Music Controls ── */}
            {!broadcasting && (
                <div style={{
                    padding: '16px 20px', borderRadius: 14,
                    background: musicMode !== 'off' ? 'color-mix(in srgb, var(--mom-primary) 6%, transparent)' : 'var(--mom-cream)',
                    border: musicMode !== 'off' ? '1.5px solid var(--mom-primary)' : '1.5px solid var(--mom-cream-dark)',
                    transition: 'all 0.2s',
                }}>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: 'var(--mom-charcoal)', marginBottom: 10 }}>Background Music</p>

                    {/* Mode selector */}
                    <div style={{ display: 'flex', gap: 6, marginBottom: musicMode !== 'off' ? 14 : 0, flexWrap: 'wrap' }}>
                        {[
                            { key: 'off', label: 'Off' },
                            { key: 'computer', label: 'Computer Audio' },
                            { key: 'file', label: 'Music File' },
                        ].map((mode) => (
                            <button key={mode.key}
                                onClick={() => setMusicMode(mode.key)}
                                disabled={mode.key === 'computer' && !isComputerAudioSupported}
                                style={{
                                    padding: '8px 16px', borderRadius: 10, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
                                    background: musicMode === mode.key ? 'var(--mom-primary)' : 'var(--mom-cream-dark)',
                                    color: musicMode === mode.key ? 'var(--mom-white)' : 'var(--mom-charcoal)',
                                    opacity: (mode.key === 'computer' && !isComputerAudioSupported) ? 0.4 : 1,
                                    transition: 'all 0.2s',
                                }}>
                                {mode.label}
                            </button>
                        ))}
                    </div>

                    {/* Computer Audio description */}
                    {musicMode === 'computer' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-warm-gray)', lineHeight: 1.5 }}>
                                Play music from any app on your computer (Spotify, Apple Music, YouTube, etc.).
                                Your screen video won't be broadcast, only the audio.
                            </p>
                            <div style={{ padding: '10px 14px', borderRadius: 10, background: 'color-mix(in srgb, var(--mom-primary) 10%, transparent)', border: '1px solid var(--mom-primary)' }}>
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 700, color: 'var(--mom-primary)', lineHeight: 1.5, margin: 0 }}>
                                    Remember: When the share dialog appears, toggle "Share audio" ON before clicking Share.
                                </p>
                            </div>
                            {/* ── Mix autopilot note (2026-04-24 same-day audio revision) ──
                                Replaces the prior "Music Volume · N%" slider. That slider was a
                                gain MULTIPLIER on the incoming computer-audio signal, so the
                                admin's system volume scaled the broadcast mix. Now: heavy
                                compression on the music path + fixed -12 dB bus gain keeps
                                the broadcast mix consistent regardless of system/app volume.
                                Admin's own speakers are free to be whatever loudness they want. */}
                            <div style={{ padding: '10px 14px', borderRadius: 10, background: 'var(--mom-cream)', border: '1px solid var(--mom-cream-dark)' }}>
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-charcoal)', margin: 0, lineHeight: 1.5 }}>
                                    <strong>Auto-leveled mix:</strong> the broadcast keeps your voice clearly on top of the music, no matter how loud your system or music app is. Set your own speakers or earpiece to whatever is comfortable — it won't affect what viewers hear.
                                </p>
                            </div>
                            {!isComputerAudioSupported && (
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-danger, #c46b6b)' }}>
                                    Computer audio capture is not supported in Safari. Please use Chrome or Edge.
                                </p>
                            )}
                        </div>
                    )}

                    {/* File upload */}
                    {musicMode === 'file' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <div>
                                <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: 'var(--mom-charcoal)', marginBottom: 4, display: 'block' }}>
                                    Music File {musicFileName && <span style={{ fontWeight: 400, color: 'var(--mom-warm-gray)' }}>· {musicFileName}</span>}
                                </label>
                                <input type="file" accept="audio/*" onChange={handleMusicFile}
                                    style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-charcoal)' }} />
                            </div>
                            {/* ── Mix autopilot note (2026-04-24 same-day audio revision) ──
                                Replaces the prior per-mode music-volume slider. See the
                                Computer Audio block above for the full rationale. Same
                                pipeline applies: music runs through a heavy compressor
                                that flattens variable file loudness before a fixed
                                -12 dB bus places it under voice. */}
                            <div style={{ padding: '10px 14px', borderRadius: 10, background: 'var(--mom-cream)', border: '1px solid var(--mom-cream-dark)' }}>
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-charcoal)', margin: 0, lineHeight: 1.5 }}>
                                    <strong>Auto-leveled mix:</strong> the broadcast keeps your voice clearly on top of the music no matter how loud or quiet the source file is. No knobs to adjust — just pick a track and record.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ── Test Mix button (2026-04-25) ──
                Pre-broadcast mix verification. Builds the SAME audio pipeline
                as startBroadcast but with no MediaRecorder / no destination —
                pure silent monitoring driving the meter panel below. For
                Computer Audio mode, the click triggers getDisplayMedia (which
                requires a user gesture), so this button is the deliberate
                entry point — we can't auto-start the test on mount. Only
                shown when not broadcasting and not connecting. Hidden during
                actual broadcast (the meters below already show the live mix). */}
            {!broadcasting && !connecting && !finishing && (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                        type="button"
                        onClick={testingMix ? stopTestMix : startTestMix}
                        title={testingMix ? 'Stop the audio test' : 'Build a silent preview of your mix so you can verify levels before broadcasting'}
                        style={{
                            padding: '8px 16px', borderRadius: 10,
                            fontSize: 12, fontWeight: 600,
                            fontFamily: 'var(--mom-font-sans)',
                            border: testingMix ? '1.5px solid var(--mom-primary)' : '1.5px solid var(--mom-cream-dark)',
                            cursor: 'pointer',
                            background: testingMix ? 'color-mix(in srgb, var(--mom-primary) 10%, transparent)' : 'var(--mom-cream)',
                            color: testingMix ? 'var(--mom-primary)' : 'var(--mom-charcoal)',
                            transition: 'all 0.2s',
                        }}>
                        {testingMix ? 'Stop test' : 'Test mix'}
                    </button>
                </div>
            )}

            {/* ── Audio level meters — broadcast OR test (2026-04-24 rev 2 + 2026-04-25 test) ──
                Two stacked 10-bar meters: VOICE (post-limiter voice path) and
                MUSIC/COMPUTER (post-limiter music path; omitted in 'off' mode).
                Renders when actually broadcasting OR when the admin clicked
                "Test mix" pre-broadcast. The meter signal is identical between
                test and broadcast — same pipeline, same analyser tap location
                — so what you see during test IS what viewers will see/hear
                during broadcast. Flat-zero on a meter is the instant cue that
                that path broke (mic disconnect, source paused, Share-audio
                not toggled, etc). */}
            {((broadcasting && musicPlaying) || testingMix) && (() => {
                // Build the meter row list. Voice always shown. Music only
                // shown when there's a music source (not 'off' mode).
                const meterRows = [{ label: 'Voice', level: voiceLevel }];
                if (musicMode !== 'off') {
                    meterRows.push({ label: musicMode === 'computer' ? 'Computer' : 'Music', level: audioLevel });
                }
                const hintText = testingMix
                    ? (musicMode === 'off'
                        ? 'Test mode · voice is auto-leveled for the broadcast. Click Stop test or Start broadcasting when ready.'
                        : 'Test mode · this is exactly what your broadcast mix will look like. Click Stop test or Start broadcasting when ready.')
                    : 'Auto-leveled — your voice sits on top of the music in the broadcast regardless of source loudness.';
                return (
                    <div style={{
                        display: 'flex', flexDirection: 'column', gap: 10,
                        padding: '10px 16px', borderRadius: 12,
                        background: 'var(--mom-cream)',
                        border: testingMix ? '1.5px solid color-mix(in srgb, var(--mom-primary) 35%, transparent)' : 'none',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: 'var(--mom-charcoal)', whiteSpace: 'nowrap' }}>
                                Audio levels
                            </span>
                            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: 'var(--mom-warm-gray)', flex: 1 }}>
                                {hintText}
                            </span>
                        </div>
                        {meterRows.map((meter) => (
                            <div key={meter.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 600, color: 'var(--mom-warm-gray)', letterSpacing: '0.08em', textTransform: 'uppercase', minWidth: 60 }}>
                                    {meter.label}
                                </span>
                                <div style={{ display: 'flex', gap: 2, alignItems: 'center', flex: 1, height: 10 }}>
                                    {[...Array(10)].map((_, i) => {
                                        const threshold = i / 10;
                                        const active = meter.level > threshold;
                                        const hot = i >= 8;
                                        return (
                                            <div key={i} style={{
                                                flex: 1,
                                                height: 10,
                                                borderRadius: 2,
                                                background: active
                                                    ? (hot ? '#eab308' : 'var(--mom-success, #16a34a)')
                                                    : 'rgba(0,0,0,0.08)',
                                                transition: 'background 60ms linear',
                                            }} />
                                        );
                                    })}
                                </div>
                                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, color: meter.level < 0.01 ? 'var(--mom-danger, #c46b6b)' : 'var(--mom-warm-gray)', minWidth: 56, textAlign: 'right', fontWeight: 600 }}>
                                    {meter.level < 0.01 ? 'SILENT' : 'Flowing'}
                                </span>
                            </div>
                        ))}
                    </div>
                );
            })()}

            {/* ── D-054: Reconnecting banner ── */}
            {/* Shown during the 3-minute auto-reconnect window. Admin sees a warm
                "we're working on it" message instead of the previous hard error; if
                the backoff schedule exhausts, we fall through to the error banner below. */}
            {reconnecting && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 14px', borderRadius: 10,
                    background: 'color-mix(in srgb, var(--mom-primary) 10%, transparent)',
                    border: '1px solid color-mix(in srgb, var(--mom-primary) 35%, transparent)',
                }}>
                    <div style={{
                        width: 16, height: 16, borderRadius: '50%',
                        border: '2.5px solid color-mix(in srgb, var(--mom-primary) 30%, transparent)',
                        borderTopColor: 'var(--mom-primary)',
                        animation: 'spin 0.8s linear infinite',
                        flexShrink: 0,
                    }} />
                    <div style={{ flex: 1 }}>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: 'var(--mom-charcoal)', margin: 0 }}>
                            Reconnecting to the stream…
                        </p>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: 'var(--mom-warm-gray)', margin: 0, marginTop: 2 }}>
                            Keeping your viewers connected. Giving up in {Math.ceil(reconnectRemainingMs / 1000)}s.
                        </p>
                    </div>
                </div>
            )}

            {/* ── Error message ── */}
            {error && (
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
                    padding: '10px 14px', borderRadius: 10,
                    background: 'color-mix(in srgb, var(--mom-danger, #c46b6b) 10%, transparent)',
                    border: '1px solid color-mix(in srgb, var(--mom-danger, #c46b6b) 35%, transparent)',
                }}>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-danger, #c46b6b)', margin: 0, flex: '1 1 auto' }}>{error}</p>
                    {!broadcasting && !connecting && !finishing && (
                        <button
                            onClick={() => { setError(''); startBroadcast(); }}
                            style={{
                                padding: '6px 14px', borderRadius: 8, border: 'none',
                                background: 'var(--mom-charcoal)', color: '#fff',
                                fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600,
                                cursor: 'pointer', whiteSpace: 'nowrap',
                            }}>
                            Try again
                        </button>
                    )}
                </div>
            )}

            {/* ── Pre-recorded observability panel (2026-04-23) ──
                Heartbeat + recorder/track error surface. Rendered only for
                pre-recorded classes during active broadcasting. The prior
                implementation had NO signal that recording had silently
                stalled (the red BROADCASTING chip kept ticking on wall-clock
                time even after MediaRecorder had given up). This panel reads
                lastChunkAtRef directly (no state coupling) and re-renders on
                heartbeatTick — re-render cadence decoupled from the 4 Hz
                chunk arrival rate.

                States:
                  • Normal: "Captured: N MB · last frame X.Xs ago" in a calm
                    neutral chip, reassuring admin that writes are flowing.
                  • Stalled: when last chunk ≥ HEARTBEAT_STALE_MS ago AND
                    no error is set, show an amber warning. The admin should
                    click End Class to save what's on disk.
                  • Errored: when recorder.onerror or track .onended fired,
                    show recorderErrorMsg in red, with a "Save now" CTA
                    that routes through the standard End Class flow. */}
            {broadcasting && classTypeRef.current === 'prerecorded' && (() => {
                const now = Date.now();
                const lastChunkAgo = lastChunkAtRef.current ? Math.max(0, now - lastChunkAtRef.current) : 0;
                const totalMb = (totalBytesCapturedRef.current / (1024 * 1024)).toFixed(1);
                const stalled = lastChunkAtRef.current > 0 && lastChunkAgo > HEARTBEAT_STALE_MS;
                const errored = !!recorderErrorMsg;
                const alarm = errored || stalled;
                const palette = errored
                    ? { bg: 'color-mix(in srgb, var(--mom-danger, #c46b6b) 10%, transparent)', border: 'color-mix(in srgb, var(--mom-danger, #c46b6b) 35%, transparent)', fg: 'var(--mom-danger, #c46b6b)' }
                    : stalled
                        ? { bg: 'color-mix(in srgb, #eab308 10%, transparent)', border: 'color-mix(in srgb, #eab308 45%, transparent)', fg: '#9a7b0a' }
                        : { bg: 'var(--mom-cream)', border: 'var(--mom-cream-dark)', fg: 'var(--mom-charcoal)' };
                // Info line (always shown) — ground truth about chunk flow so
                // admin can ALWAYS tell whether the recording is still capturing,
                // even when the alarm line above it says otherwise. Without this,
                // a latched alarm message with zero info about actual chunks
                // makes it impossible to distinguish "real disconnect, save now"
                // from "transient track event, recording is fine, keep going".
                const infoLine = `Captured ${totalMb} MB · last frame ${(lastChunkAgo / 1000).toFixed(1)}s ago`;
                // Alarm line (only when errored or stalled) — the *urgent* message
                // rendered in the alarm palette above the info line.
                const alarmLine = errored
                    ? recorderErrorMsg
                    : stalled
                        ? `⚠ No frames captured for ${(lastChunkAgo / 1000).toFixed(1)}s — recording may have stalled.`
                        : null;
                return (
                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
                        padding: '10px 14px', borderRadius: 10,
                        background: palette.bg,
                        border: `1px solid ${palette.border}`,
                    }}>
                        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                            {alarmLine && (
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: palette.fg, margin: 0 }}>
                                    {alarmLine}
                                </p>
                            )}
                            <p style={{
                                fontFamily: 'var(--mom-font-sans)',
                                fontSize: alarmLine ? 11 : 13,
                                color: alarmLine ? 'var(--mom-warm-gray)' : palette.fg,
                                margin: alarmLine ? '4px 0 0' : 0,
                                fontStyle: alarmLine ? 'italic' : 'normal',
                            }}>
                                {infoLine}
                                {/* hidden tick so react counts the render — prevents the
                                    linter flagging heartbeatTick as unused */}
                                <span style={{ display: 'none' }}>{heartbeatTick}</span>
                            </p>
                        </div>
                        {alarm && typeof onEndClassRequest === 'function' && (
                            <button
                                onClick={() => onEndClassRequest()}
                                style={{
                                    padding: '6px 14px', borderRadius: 8, border: 'none',
                                    background: 'var(--mom-charcoal)', color: '#fff',
                                    fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600,
                                    cursor: 'pointer', whiteSpace: 'nowrap',
                                }}>
                                Save now
                            </button>
                        )}
                    </div>
                );
            })()}

            {/* ── Recorder error banner (live only, 2026-04-24 live-parity pass) ──
                `recorder.onerror` and quota-write errors set `recorderErrorMsg` via
                the shared buildRecorder (see ~L575). The pre-recorded observability
                panel above renders that state with a heartbeat context + "Save now"
                CTA. For live classes there's no local blob to "save" and the prior
                gap was: that state was wired but NEVER surfaced during a live
                broadcast — admin would see the red "BROADCASTING · 12:34" chip
                keep ticking on wall-clock while chunks had silently stopped
                reaching the relay. This banner closes that gap with a dedicated,
                unambiguous message + the standard End Class recovery CTA. Rendered
                only during an active LIVE broadcast; pre-recorded uses the panel
                above which already handles this state with richer context. */}
            {broadcasting && classTypeRef.current !== 'prerecorded' && recorderErrorMsg && (
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
                    padding: '10px 14px', borderRadius: 10,
                    background: 'color-mix(in srgb, var(--mom-danger, #c46b6b) 10%, transparent)',
                    border: '1px solid color-mix(in srgb, var(--mom-danger, #c46b6b) 35%, transparent)',
                }}>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: 'var(--mom-danger, #c46b6b)', margin: 0, flex: '1 1 auto' }}>
                        {recorderErrorMsg}
                    </p>
                    {typeof onEndClassRequest === 'function' && (
                        <button
                            onClick={() => onEndClassRequest()}
                            style={{
                                padding: '6px 14px', borderRadius: 8, border: 'none',
                                background: 'var(--mom-charcoal)', color: '#fff',
                                fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600,
                                cursor: 'pointer', whiteSpace: 'nowrap',
                            }}>
                            End Class
                        </button>
                    )}
                </div>
            )}

            {/* ── Tier-change toast — brief confirmation of auto-downshift ── */}
            {tierNotice && (
                <div style={{
                    padding: '10px 14px', borderRadius: 10,
                    background: 'color-mix(in srgb, var(--mom-primary) 10%, transparent)',
                    border: '1px solid color-mix(in srgb, var(--mom-primary) 35%, transparent)',
                    fontFamily: 'var(--mom-font-sans)', fontSize: 13,
                    color: 'var(--mom-charcoal)',
                    animation: 'fadeIn 0.3s ease-out',
                }}>
                    {tierNotice}
                </div>
            )}

            {/* ── Broadcast button ── */}
            {finishing ? (
                <div style={{
                    width: '100%', padding: '16px 24px', borderRadius: 14,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                    background: 'var(--mom-cream)', border: '1.5px solid var(--mom-cream-dark)',
                }}>
                    <div style={{ width: 16, height: 16, borderRadius: '50%', border: '2.5px solid var(--mom-cream-dark)', borderTopColor: 'var(--mom-primary)', animation: 'spin 0.8s linear infinite' }} />
                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: 'var(--mom-charcoal)' }}>
                        Finishing up...
                    </span>
                </div>
            ) : !broadcasting ? (
                <button onClick={startBroadcast} disabled={!canStart}
                    style={{
                        width: '100%', padding: '16px 24px', borderRadius: 14, fontSize: 16, fontWeight: 700, border: 'none', cursor: 'pointer',
                        fontFamily: 'var(--mom-font-sans)',
                        background: connecting ? 'var(--mom-warm-gray)' : '#dc2626',
                        color: '#fff',
                        opacity: canStart ? 1 : 0.5,
                        transition: 'all 0.2s',
                    }}>
                    {connecting
                        ? 'Connecting...'
                        : classType === 'prerecorded'
                            ? (musicMode === 'computer' ? 'Start Recording (will ask to share audio)' : 'Start Recording')
                            : (musicMode === 'computer' ? 'Start Broadcasting (will ask to share audio)' : 'Start Broadcasting')}
                </button>
            ) : (
                // ── Unified End Class control (both class types, 2026-04-20) ──
                // Fires the parent's onEndClassRequest → Workouts opens the confirm
                // modal (copy branches on classType inside the modal) → on confirm,
                // handleEndClass routes live classes through the seal-first flow and
                // delegates pre-recorded RECORDING phase to handleEndRecording. In both
                // cases /complete is awaited BEFORE MediaRecorder stops, so the Mux
                // manifest is sealed with #EXT-X-ENDLIST before any silent-source
                // window can open — zero slate possible. Retired the live-only
                // "Stop Broadcasting" direct stopBroadcast button for parity and to
                // remove the footgun. `stopBroadcast` itself is still invoked by the
                // endingAt effect below, by reconnect exhaustion, and on unmount.
                <button onClick={() => { if (typeof onEndClassRequest === 'function') onEndClassRequest(); }}
                    style={{
                        width: '100%', padding: '16px 24px', borderRadius: 14, fontSize: 16, fontWeight: 700, border: 'none', cursor: 'pointer',
                        fontFamily: 'var(--mom-font-sans)',
                        background: '#dc2626', color: '#fff',
                    }}>
                    End Class
                </button>
            )}

            {!broadcasting && (
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: 'var(--mom-warm-gray)', textAlign: 'center', lineHeight: 1.5 }}>
                    {musicMode === 'computer'
                        ? 'Your camera, microphone, and computer audio will be mixed and streamed live. Play music from any app.'
                        : musicMode === 'file'
                            ? 'Your camera, microphone, and music file will be mixed and streamed live.'
                            : 'Your camera and microphone will be streamed live. You can play music from a speaker in the room.'}
                </p>
            )}

            <style>{`
                @keyframes livePulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
                @keyframes spin { to { transform: rotate(360deg); } }
            `}</style>
        </div>
    );
}

export default LiveBroadcaster;
